import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type ColumnPinningState,
  type OnChangeFn,
  type Row,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import * as React from "react";

import { Box } from "../../components/ui/box";
import {
  BOX_PADDING_LEFT,
  BOX_PADDING_RIGHT,
  BOX_PADDING_X,
  type SpacingToken,
} from "../../components/ui/box-classes";
import {
  ContextMenuItem,
  ContextMenuSeparator,
} from "../../components/ui/context-menu";
import { Icon } from "../../components/ui/icon";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Text } from "../../components/ui/text";
import { useIsTouchDevice } from "../../hooks/use-is-touch-device";
import { useElementSize } from "../../hooks/use-element-size";
import {
  PRESS_SURFACE_CLASS,
  SURFACE_BG,
  SURFACE_VEIL,
  type SurfaceToken,
} from "../../components/ui/box-classes";
import { cn } from "../../lib/utils";
import { SCROLLPORT_CONTAINER_CLASS } from "../../lib/scroll-end-slack";

import { DataTableCell, DataTableCellContent } from "./data-table-cell";
import { DataTableColumnHeader } from "./data-table-column-header";
import { DataTableEmpty } from "./data-table-empty";
import {
  DataTableGroupHeader,
  DataTableSectionHeader,
} from "./data-table-group";
import { useDataTable } from "./data-table-hook";
import {
  DataTableOverlayProvider,
} from "./data-table-overlay";
import {
  DataTableRowDragGroupProvider,
  DataTableRowDragHost,
  DataTableRowDropScopeBox,
} from "./data-table-row-drag";
import {
  BULK_ACTIONS_BAR_CLEARANCE,
  BulkActionsBar,
} from "../bulk-actions-bar/bulk-actions-bar";
import {
  DataTableSurfaceProvider,
  useDataTableSurface,
  DataTableFirstHeaderProvider,
  DataTableFooterChromeProvider,
  DataTableDensityProvider,
  DataTableGroupRowsProvider,
  DataTableHeaderDragProvider,
  DataTableShiftSelectProvider,
  DataTableSelectAllProvider,
  useDataTableSelectAll,
  DataTableSharedSelectAllProvider,
  type DataTableSharedSelectAllValue,
} from "./data-table-context";
import {
  DataTableResizeHandle,
  type ColumnResizeController,
} from "./data-table-resize";
import { useDataTableGroupRows } from "./use-data-table-group-rows";
import { NARROW_CONTAINER_WIDTH, useDataTableLayout } from "./use-data-table-layout";
import {
  createActionsColumn,
} from "./data-table-actions-column";
import { DataTableRowKebabHost } from "./data-table-row-kebab";
import {
  DataTableColumnPickerProvider,
  useDataTableColumnHide,
} from "./data-table-add-column";
import { createSelectionColumn } from "./data-table-selection-column";
import type {
  DataTableColumnDef,
  DataTableColumnMeta,
  DataTableDensity,
  DataTableGroup,
  DataTableProps,
  DataTableRowDragConfig,
  DataTableSection,
} from "./data-table-types";
import type { SelectAllGroup } from "../bulk-actions-bar/bulk-actions-bar-types";
import {
  ALL_GROUP_KEY,
  DENSITY_CONFIG,
  STRUCTURAL_COLUMN_IDS,
  TABLE_TOTAL_WIDTH_VAR,
  getColumnMeta,
  getTruncationClass,
  computeColumnMove,
  PADDING_X_PX,
  isOwnPress,
  beginHold,
  pressArmed,
  SELECTION_COLUMN_WIDTH,
  type ColumnMoveTarget,
} from "./data-table-utils";
import { ROW_RUNGS } from "../../lib/row-rungs";
import type { Omittable } from "../../lib/props";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_PINNED_COLUMN_IDS = ["select"];

/** Shared min-width so rows/headers span the full resolved table width. */
const TABLE_MIN_WIDTH_STYLE: React.CSSProperties = {
  // Never narrower than the container: shrinking columns below the viewport
  // width must not pull row borders/backgrounds short of the right edge.
  minWidth: `max(100%, var(${TABLE_TOTAL_WIDTH_VAR}))`,
};

// Hoist row-model factories to module scope so TanStack Table's internal
// memoization sees stable references.  Creating these inline inside each
// group component caused the full row-model pipeline to rerun on every
// scroll-driven re-render.
const CORE_ROW_MODEL = getCoreRowModel();
const SORTED_ROW_MODEL = getSortedRowModel();

// Fallback group header height before layout measurement runs.
const DEFAULT_GROUP_HEADER_HEIGHT = ROW_RUNGS.tableGroupBand;

// Rows drawn while a group is on its very first load with nothing known —
// no data, no total. Enough to read as "a list is coming" without painting
// a page of shimmer for what may be two rows.
const INITIAL_LOADING_ROW_COUNT = 5;

// Stand-ins rendered while `loading` is set with zero groups — a grouped
// table whose group set is itself still resolving (the counts query in
// flight). Each renders a skeleton band over the initial-load skeleton rows,
// so the first paint mirrors the settled grouped page instead of a blank
// body. Module-level: stable identities keep the group-content memo held.
const SKELETON_GROUPS: DataTableGroup<never>[] = [0, 1, 2].map((i) => ({
  key: `__skeleton-${i}__`,
  label: "",
  data: [],
  loading: true,
  skeleton: true,
}));

// Band-title bar widths for skeleton groups, staggered by group position so
// the stand-in bands read like real labels of varying length.

/** Grouped tables wrap each group (header + rows) in the `tgroup` hover
 *  scope; ungrouped tables pass children through with no extra node.
 *
 *  It carries the table's min-width because it is the group band's sticky
 *  CONTAINING BLOCK, and a sticky element cannot travel outside that box. This
 *  wrapper is a flex item of the scroll container's column, where
 *  `min-width: auto` resolves to 0 — the content-based minimum applies to the
 *  MAIN axis, and the main axis here is vertical — so it stretches to exactly
 *  the scrollport width. At that width the band (itself the scrollport's width)
 *  has zero room to move and scrolls away with the columns instead of pinning.
 *  The group FOOTER band never had the bug: it sits inside the body's own
 *  min-width box, which is already table-wide. */
function GroupScopeBox({
  enabled,
  groupKey,
  dropTarget,
  children,
}: {
  enabled: boolean;
  groupKey: string;
  /** `rowDrag` table: the scope box is also the group's drop target. */
  dropTarget: boolean;
  children: React.ReactNode;
}) {
  if (!enabled) return <>{children}</>;
  if (dropTarget) {
    return (
      <DataTableRowDropScopeBox groupKey={groupKey} style={TABLE_MIN_WIDTH_STYLE}>
        {children}
      </DataTableRowDropScopeBox>
    );
  }
  return (
    <Box group="tgroup" style={TABLE_MIN_WIDTH_STYLE}>
      {children}
    </Box>
  );
}
/** Mounts the row-drag host around the groups only when the table drags. */
function RowDragBoundary<TData>({
  config,
  gutterPx,
  children,
}: {
  config: DataTableRowDragConfig<TData> | undefined;
  gutterPx: number;
  children: React.ReactNode;
}) {
  if (!config) return <>{children}</>;
  return (
    <DataTableRowDragHost config={config} gutterPx={gutterPx}>
      {children}
    </DataTableRowDragHost>
  );
}

// A section card's horizontal footprint: its side margins plus a 1px border
// each side — what the groups' pinned bands give back so they end at the
// card's inner edge instead of overhanging it.
const SECTION_MARGIN_X = "md" satisfies SpacingToken;
const SECTION_BAND_INSET_PX = PADDING_X_PX[SECTION_MARGIN_X] * 2 + 2;

// Stable no-op so `useWindowedPages` always gets a fetchPage identity for groups
// without one (the `enabled` flag gates actual fetching).
const HEADER_TEXT_CLASS = "text-xs font-medium text-faint-foreground";

// Trailing filler after a row's last cell: packed layouts can end the columns
// short of the container's right edge, but the row's bottom border must still
// run the full width — cells own their borders, so the leftover
// span draws its own. Zero-width whenever the columns fill/overflow the row.
const ROW_FILLER_CLASS = "flex-1 border-b border-border/60";
const ROW_FILLER_LAST_CLASS = "flex-1";

/**
 * Stable element type for BODY cell content — the reason cells survive a
 * consumer rebuilding its column defs.
 *
 * TanStack's `flexRender` does `createElement(cellFn, ctx)`, which makes the
 * renderer function ITSELF the component type. Consumer `cell:` renderers are
 * inline arrows inside a `columns` memo (the TanStack idiom), so any dep churn
 * mints a new function ⇒ a new type ⇒ React UNMOUNTS and remounts the whole
 * cell subtree. That silently resets every piece of cell-local state mid-
 * interaction: an open editor popover closes after one pick, so a multi-select
 * cell can never take a second value. Headers keep `flexRender` on purpose —
 * the header path detects component-typed content by its element type (see
 * renderHeader), which this slot would mask.
 *
 * Invoking the renderer INSIDE this component keeps the element type constant
 * while preserving per-cell hook state (one slot instance per `cell.id`).
 */
function DataTableCellSlot({
  render,
  ctx,
}: {
  render: unknown;
  ctx: unknown;
}): React.ReactNode {
  // Only PLAIN function renderers have the unstable-identity problem — they
  // are the inline arrows a columns memo re-mints. Classes must be MOUNTED,
  // not called, and their identity (like exotics' and raw nodes') is already
  // stable, so everything else keeps flexRender's exact semantics.
  if (
    typeof render === "function" &&
    !(render as { prototype?: { isReactComponent?: boolean } }).prototype
      ?.isReactComponent
  ) {
    return <>{(render as (props: unknown) => React.ReactNode)(ctx)}</>;
  }
  return (
    <>
      {flexRender(
        render as Parameters<typeof flexRender>[0],
        ctx as Parameters<typeof flexRender>[1],
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Surface recipes
//
// The table names ONE surface (the `surface` prop, `background` by default) and
// every part reads it from context, so the header, the pinned cells and the
// row washes cannot drift apart. The washes are the foreground color at low alpha, so a hovered
// row on a card and the same row on a dialog both read as the same wash rather
// than as a hole.
//
// The row-state classes repeat the base TableRow stock modifier chains
// (`not-in-data-[variant=card]:hover:` and the `dark:` twins) EXACTLY —
// The `not-in-data-[variant=card]` chain is part of each class: tailwind-merge
// only drops a stock class when the replacement's modifier chain is an
// identical string, and a bare `hover:` override would otherwise lose to the
// stock `dark:` variants. So these are written out whole rather than composed.
// ---------------------------------------------------------------------------

// Selected rows carry the workspace accent instead of the neutral wash: a
// `--brand`-tinted wash (alpha, so it composites on any surface fill and the
// pinned cells replay it like every wash), one step stronger under hover,
// and full-bleed accent hairlines. Every row boundary is exactly ONE line:
// the overlay pseudo runs from -top-px to bottom-0, so its top border lands
// on the PREVIOUS row's border pixel and its bottom border on its own — two
// adjacent selected rows therefore paint the same shared pixel instead of
// stacking a second line. The line color is OPAQUE (brand mixed into the
// table fill, not alpha) so it REPLACES the neutral hairline under it —
// an alpha line over the translucent dark-mode border composites into a
// visibly paler two-tone band. z-10 keeps it over the pinned cells' opaque
// fills; a sticky header (z-20, opaque) still covers the -1px tip when a
// group's first row is selected, which correctly yields one neutral line.
// The wash overrides repeat TableRow's stock modifier chains exactly (see
// the tailwind-merge note above).
const ROW_SELECTED_CLASS = cn(
  "not-in-data-[variant=card]:data-selected:[--row-wash:color-mix(in_oklab,var(--brand)_10%,transparent)]",
  "data-selected:after:pointer-events-none data-selected:after:absolute data-selected:after:-top-px data-selected:after:inset-x-0 data-selected:after:bottom-0 data-selected:after:z-10 data-selected:after:border-y data-selected:after:[border-color:color-mix(in_oklab,var(--brand)_45%,var(--table-fill,var(--background)))]",
);
const ROW_SELECTED_HOVER_CLASS =
  "not-in-data-[variant=card]:data-selected:hover:[--row-wash:color-mix(in_oklab,var(--brand)_15%,transparent)]";
// Data rows hover quieter than the stock wash: table cells draw hairline
// chrome ON the hovered row (the value-cell ring, `hover:border-border`), and
// in dark mode the full wash lifts the row to nearly the border token's own
// lightness, erasing that ring. 60% of the wash keeps the hover legible while
// the hairlines stay visible over it.
const ROW_HOVER_SOFT_CLASS =
  "not-in-data-[variant=card]:hover:[--row-wash:color-mix(in_oklab,var(--wash-hover)_60%,transparent)]";
// Touch has no hover, so a HELD press stands in for it: a finger held on a
// row ARMS it (`data-armed`, `lib/touch-arm`), which paints the hover wash
// and reveals the row's hover chrome (checkbox, kebab, inline actions) for
// the next tap to use; the click that release produces is spent on the
// reveal — the row does not open, the name cell does not open. A tap is the
// press, exactly as a click. A press anywhere outside disarms it. A mouse
// never arms.
const ROW_ARMED_CLASS =
  "not-in-data-[variant=card]:data-armed:[--row-wash:color-mix(in_oklab,var(--wash-hover)_60%,transparent)]";
/**
 * A row is a rectangle, so the buttons inside it are squared by default rather
 * than the Button's stadium — a capsule in a 32px row reads as a control
 * dropped on the row instead of chrome belonging to it. This is a DEFAULT, not
 * a lock: Button stamps `data-shape="default"` only when the call site left
 * the shape unnamed, so any `shape` prop (`pill` included) drops out of this
 * selector and keeps the geometry it asked for. The selector keys on
 * `data-shape`, never `data-slot=button`: a Popover, Tooltip or Menu trigger
 * rendered into the Button replaces `data-slot` with its own, and a rule read
 * through it silently misses every row button that opens something.
 *
 * It has to be a descendant rule rather than a prop. A row's buttons come from
 * consumer cell renderers this package never sees, and the roster grows every
 * time a host adds a column — the same reason `surface` and `density` travel
 * by context. Portaled popups are exempt for free: a menu opened FROM a row
 * lives under `document.body`, so it is not a descendant and keeps stock
 * Button geometry.
 */
const ROW_BUTTON_SHAPE_CLASS =
  "[&_[data-shape=default]]:rounded-md [&_[data-shape=default]]:before:rounded-md";
/** Header rows keep the stock chains but paint nothing — thead owns the bg. */
const ROW_STATES_OFF_CLASS =
  "not-in-data-[variant=card]:hover:[--row-wash:transparent] not-in-data-[variant=card]:has-[[data-popup-open]]:[--row-wash:transparent] not-in-data-[variant=card]:data-selected:[--row-wash:transparent]";

/**
 * A pinned cell paints its own opaque fill — the scrolling columns pass BEHIND
 * it — and then replays the row's wash as a layer on top of that fill, so the
 * pinned block and the scrolling block land on the same composited color. The
 * fill is `SURFACE_BG[surface]` in `background-color`; this is the layer.
 */
const PINNED_CELL_WASH_CLASS =
  "bg-linear-to-b from-(--row-wash) to-(--row-wash)";

/**
 * The table's fill as a VALUE, declared once on the scroll container and
 * inherited by every row and cell. Nothing downstream names a surface: a sticky
 * cell and its fade both paint `--table-fill` under `--row-wash`, so all three
 * layers change together with the state and the `surface` prop.
 */
const SURFACE_FILL_VAR: Record<SurfaceToken, string> = {
  sunken: "[--table-fill:var(--sunken)]",
  background: "[--table-fill:var(--background)]",
  card: "[--table-fill:var(--card)]",
  elevated: "[--table-fill:var(--elevated)]",
  popover: "[--table-fill:var(--popover)]",
  sidebar: "[--table-fill:var(--sidebar)]",
};


/**
 * The right lane's mirror: the FIRST right-pinned header cell (the actions
 * column, home of the circle-plus column picker) dissolves at its LEADING edge,
 * so a column label panning under it ghosts into the sticky glyph instead of
 * hard-cutting at the cell boundary.
 *
 * This one cannot be a background image. The cell is 60px and the glyph starts
 * ~15px in, so a fade INSIDE the cell would either be a 12px step nobody reads
 * as a fade or run under the glyph and let the label bleed through it. The
 * fade instead rides a `::before` that reaches 24px PAST the leading edge —
 * transparent there, opaque by the cell boundary — so the whole ramp plays out
 * over the neighbour and the glyph always sits on solid fill. `-z-10` keeps the
 * pseudo under the cell's in-flow content (the sticky cell is its own stacking
 * context), and it stops 1px short of the bottom so the header line beneath
 * stays whole. The actions column opts out of `overflow-hidden` for exactly
 * this overhang (see createActionsColumn).
 *
 * The overhang also lands on the ABUTTING column's trailing 24px — where its
 * hover wash ends and its resize seam lives — and would dim both to nothing.
 * So the fade yields while that column is in hand: the pseudo goes transparent
 * when the previous header cell is hovered or holds `data-dtresizing` (pointer
 * capture takes :hover off the cell mid-drag), and the wash then cuts hard at
 * the lane edge with the seam line whole.
 *
 * The hover yield is gated on the lane actually ABUTTING that cell — the
 * scroll container carries `data-dtlaneabut` while there is no horizontal
 * overflow or the table is scrolled to its end (see the effect that stamps
 * it). Mid-scroll the DOM-previous column is the one panning UNDER the lane:
 * lifting the fade for its hover would paint its wash straight through the
 * circle-plus, which is what happened when a column was widened past the
 * edge. The resize yield stays ungated — a seam drag is the one time the
 * column under the lane must read whole.
 */
const HEADER_FADE_RIGHT_CLASS =
  "before:pointer-events-none before:absolute before:top-0 before:bottom-px before:right-0 before:-left-6 before:-z-10 before:bg-[linear-gradient(to_right,transparent_0,var(--table-fill)_24px)] before:transition-opacity before:duration-150 [[data-dtlaneabut]_[data-dthcell]:hover+&]:before:opacity-0 [[data-dthcell][data-dtresizing]+&]:before:opacity-0";

/**
 * The left-pinned lane may not be wider than the scrollport, and this is
 * geometry rather than taste: `position: sticky` is clamped to its containing
 * block — for a pinned cell, the row — so a cell can only hold its offset while
 * the lane in front of it fits on screen. Once `select + name` exceeds the
 * scrollport, the last pinned cell runs out of travel at full scroll, slides
 * left over its neighbours, and (being opaque and later in DOM at the same z)
 * paints the checkbox out. Measured: a 664px lane in a 600px port put the name
 * cell at -20 instead of 44, covering the checkbox by 64px.
 *
 * So the lane is capped by shrinking its LAST column, which keeps every pin
 * honest and truncates that column instead. Only a narrow viewport with a wide
 * pinned column reaches this; a normal desktop lane is far inside the cap.
 */

/**
 * The body's right-edge fade: the right-pinned actions cell of every row.
 * It holds no content (the kebab lives in the leading content cell), so the
 * whole 60px cell is a ramp from transparent to the row's paint, and a chip
 * or avatar panning under it ghosts out toward the scroll edge instead of
 * hard-cutting — the "more columns this way" hint. It is inert to the
 * pointer, so the cell beneath stays clickable through it, and it lifts on
 * row hover so the value under the hand reads whole.
 *
 * Why a ramp is safe HERE and not on the left lane: the row's paint is two
 * layers — `--table-fill` under the alpha `--row-wash` — and this ramps BOTH
 * in step, so the edge lands on exactly the row's colour on a resting,
 * hovered or selected row alike. A single ramped fill layer (the left lane's
 * blur that was removed) composites the page colour over the wash and smears
 * a band in the wash colour; ramping the wash with it is what the left lane
 * could not do with a shadow. When the lane abuts the last column (no
 * overflow, or scrolled to the end) the cell sits after the columns over
 * nothing, and the ramp is invisible by construction.
 */
const BODY_EDGE_FADE_CLASS =
  "pointer-events-none transition-opacity duration-150 group-hover/row:opacity-0 bg-[linear-gradient(to_right,transparent,var(--row-wash)),linear-gradient(to_right,transparent,var(--table-fill))]";

// The trailing edge of the left-pinned block is a HARD cut. A blurred shadow
// there cannot be correct: it replays the cell's opaque `--table-fill`, and a
// blur ramps that fill's own alpha, so every partially-opaque step composites
// the page color OVER whatever the row is painted — on a hovered or selected
// row that reads as a smeared band in the wash color, worst exactly where the
// wash is (the artifact this replaced). The fill can only blend where it is
// fully opaque, which a blur guarantees it is not.

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAlignmentClass(
  meta?: DataTableColumnMeta,
  isSelectionColumn?: boolean,
) {
  if (isSelectionColumn) return "justify-center";
  if (meta?.isNumeric) return "justify-end";
  return "justify-start";
}

// Stable empty references to avoid re-creating tables on every render
const EMPTY_SORTING: SortingState = [];
const EMPTY_ROW_SELECTION: RowSelectionState = {};

/**
 * "Hide column" entry of the header context menu. A component rather than
 * inline items so the picker-context read happens when the menu OPENS — the
 * popup renders inside `DataTableColumnPickerProvider`, while the header
 * build that composes the menu does not. Renders nothing when the table
 * wired no `onColumnVisibilityChange` or the column is locked (pinned /
 * `enableHiding: false`); hiding the last visible column disables instead.
 */
function HideColumnMenuItem({
  columnId,
  withSeparator,
}: {
  columnId: string;
  /** False when the hide item would be the menu's only section. */
  withSeparator: boolean;
}) {
  const hideControl = useDataTableColumnHide(columnId);
  if (!hideControl) return null;
  return (
    <>
      {withSeparator ? <ContextMenuSeparator /> : null}
      <ContextMenuItem
        disabled={hideControl.disabled}
        onClick={hideControl.hide}
      >
        <Icon name="eye-closed" size="lg" color="muted" />
        <Text size="sm">Hide column</Text>
      </ContextMenuItem>
    </>
  );
}

// ---------------------------------------------------------------------------
// DataTableGroupContent — per-group sub-table with its own virtualizer
// ---------------------------------------------------------------------------

type DataTableGroupContentProps<TData> = Omittable<{
  group: DataTableGroup<TData>;
  groupKey: string;
  columns: DataTableColumnDef<TData>[];
  isExpanded: boolean;
  density: DataTableDensity;
  getRowId?: (row: TData, index: number) => string;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  canSelectRow?: (row: TData) => boolean;
  /** Shared TanStack state — same references for every group. */
  columnPinning: ColumnPinningState;
  tableColumnOrder: string[];
  tableColumnVisibility: VisibilityState;
  /** Per-column-id styles (CSS-var widths + sticky offsets), built once in the outer table. */
  headerCellStyles: Map<string, React.CSSProperties>;
  bodyCellStyles: Map<string, React.CSSProperties>;
  /** Column resize drag controller (stable identity). */
  resizeController: ColumnResizeController;
  activeResizeColumnId: string | null;
  /** Column id currently being drag-reordered (dims its header). */
  draggingColumnId: string | null;
  canResizeColumns: boolean;
  columnLayout: "fill" | "packed";
  /** Select-column leading alignment for the group header (null = natural flow). */
  groupLeading: { width: number; chevronMargin: number } | null;
  reorderableIdSet: ReadonlySet<string>;
  /** Reorderable column ids in on-screen order — drives the header context
   *  menu's move targets and edge-disabled states. */
  reorderableIds: readonly string[];
  /** Commits a discrete column move in drag-drop terms (insert before
   *  `beforeId`; null = end of the reorderable span). */
  onColumnMove: (columnId: string, beforeId: string | null) => void;
  /** Consumer section of the header context menu — see DataTableProps. */
  renderColumnMenuItems?: ((columnId: string) => React.ReactNode) | undefined;
  /** Whether the table wired `onColumnVisibilityChange` — gates the header
   *  menu's "Hide column" section (per-column lock checks live in the item). */
  canHideColumns: boolean;
  containerWidth: number;
  loading?: boolean;
  emptyState?: React.ReactNode;
  onRowClick?: (row: Row<TData>) => void;
  rowClickable?: (row: Row<TData>) => boolean;
  extraData?: unknown;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  // Group header rendering
  groupTitle?: (groupKey: string) => React.ReactNode;
  /** Title node owns its own interaction — see DataTableProps.groupTitleInteractive. */
  groupTitleInteractive?: (groupKey: string) => boolean;
  /** Per-group "+ Add …" footer row — see DataTableProps.groupFooter. */
  groupFooter?: (groupKey: string) => React.ReactNode;
  groupActions?: (
    context: import("./data-table-types").DataTableGroupActionsContext<TData>,
  ) => React.ReactNode;
  onToggle: (key: string) => void;
  collapseAll: () => void;
  expandAll: () => void;
  allCollapsed: boolean;
  stickyHeader: boolean;
  scrollMode: "internal" | "external" | "fit";
  /** Suppress the column-header row. */
  hideHeader?: boolean;
  /** Suppress the bottom border under the column-header row. */
  hideHeaderBorder?: boolean;
  /** Hoist ONE column header above the first group — see DataTableProps. */
  sharedHeader?: boolean;
  /** Keep the bottom divider on each group's last row — see DataTableProps. */
  showLastRowBorder?: boolean;
  /** Render a bottom divider under each group's section band. */
  groupHeaderBorder?: boolean;
  /** Trailing gap between groups. */
  groupGap: "md" | "lg" | "xl";
  isUngrouped: boolean;
  hideGroupCount?: boolean;
  paddingX?: SpacingToken;
  paddingXAlign?: "edge" | "content";
  renderRowActions?: (row: TData) => React.ReactNode;
  /** A held press arms the row — see DataTableProps.pressOnlyOnTouch. */
  armRowsOnHold: boolean;
  /** The table mounts `rowDrag`: this group is a drop target and hosts grips. */
  rowDragEnabled: boolean;
  groupIndex: number;
  /**
   * Horizontal room a section card takes off the scroll container — its
   * margins and borders. The group's pinned bands size to the container
   * width, so inside a card they shrink by this much or overhang its edge.
   */
  bandInsetPx: number;
}>;

/**
 * Placeholder body for groups that haven't loaded data yet.
 * Uses IntersectionObserver to call onVisible when scrolled into the viewport.
 */
function PlaceholderBody({
  height,
  onVisible,
  ref,
  className,
  children,
  footer,
}: Omittable<{
  height: number;
  onVisible?: () => void;
  ref?: React.Ref<HTMLDivElement>;
  className?: string;
  children?: React.ReactNode;
  /** Below the reserved rows, where the group's footer settles. */
  footer?: React.ReactNode;
}>) {
  const innerRef = React.useRef<HTMLDivElement | null>(null);
  const firedRef = React.useRef(false);

  React.useImperativeHandle(ref, () => innerRef.current!);

  React.useEffect(() => {
    if (!onVisible || firedRef.current) return;
    // Don't observe zero-height elements — they're always "intersecting"
    if (height <= 0) return;
    const el = innerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !firedRef.current) {
          firedRef.current = true;
          onVisible();
          observer.disconnect();
        }
      },
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the element and height are read when the effect runs; onVisible is the trigger
  }, [onVisible]);

  // The scroll container is a flex column, so a plain block would shrink to
  // nothing under it and the group would reserve no scroll space at all —
  // the sentinel would then sit at the very bottom and never intersect.
  return (
    // No overflow: it would make this box the sticky header's scroll parent.
    <Box ref={innerRef} shrink={false} className={className}>
      <Box style={{ height }}>{children}</Box>
      {footer}
    </Box>
  );
}

function DataTableGroupContentInner<TData>({
  group,
  groupKey,
  columns,
  isExpanded,
  density,
  getRowId,
  sorting,
  onSortingChange,
  rowSelection,
  onRowSelectionChange,
  canSelectRow,
  columnPinning,
  tableColumnOrder,
  tableColumnVisibility,
  headerCellStyles,
  bodyCellStyles,
  resizeController,
  activeResizeColumnId,
  draggingColumnId,
  canResizeColumns,
  columnLayout,
  groupLeading,
  reorderableIdSet,
  reorderableIds,
  onColumnMove,
  renderColumnMenuItems,
  canHideColumns,
  containerWidth,
  loading: tableLoading,
  emptyState,
  onRowClick,
  rowClickable,
  extraData,
  scrollContainerRef,
  groupTitle,
  groupTitleInteractive,
  groupFooter,
  groupActions,
  onToggle,
  collapseAll,
  expandAll,
  allCollapsed,
  stickyHeader,
  scrollMode,
  hideHeader,
  hideHeaderBorder,
  sharedHeader,
  showLastRowBorder,
  groupHeaderBorder,
  groupGap,
  isUngrouped,
  hideGroupCount,
  paddingX,
  paddingXAlign,
  renderRowActions,
  armRowsOnHold,
  rowDragEnabled,
  groupIndex,
  bandInsetPx,
}: DataTableGroupContentProps<TData>) {
  const bandWidth = containerWidth - bandInsetPx;
  const surface = useDataTableSurface();
  const densityConfig = DENSITY_CONFIG[density];
  const rowHeight = densityConfig.rowHeight;
  // Only show loading state when there are truly no rows yet (initial load).
  // Once rows exist, they must NEVER be hidden by a loading indicator.
  const hasData = group.data.length > 0;
  const groupLoading = Boolean(tableLoading || group.loading) && !hasData;
  // Whether this group has ever shown rows. A group loading again after it has
  // — its predicate changed under it — is not a first paint: its column header
  // stays and its placeholder rows paint at once. The first-paint gates below
  // (the header waiting for proof of a table, the delayed skeleton) exist so a
  // fast first answer never flashes a placeholder over nothing. A table that
  // already showed rows has nothing to protect that way and everything to lose:
  // the blank where its rows were IS the flash.
  const hadRowsRef = React.useRef(false);
  if (hasData) hadRowsRef.current = true;
  const reloading = groupLoading && hadRowsRef.current;

  // -------------------------------------------------------------------------
  // Sticky header config
  // -------------------------------------------------------------------------

  const stickyEnabled = stickyHeader;
  const useInternalScroll = scrollMode === "internal";
  const [groupHeaderHeight, setGroupHeaderHeight] = React.useState(
    DEFAULT_GROUP_HEADER_HEIGHT,
  );

  // -------------------------------------------------------------------------
  // Per-group TanStack Table
  // -------------------------------------------------------------------------

  const table = useDataTable({
    data: group.data,
    columns,
    getCoreRowModel: CORE_ROW_MODEL,
    getSortedRowModel: SORTED_ROW_MODEL,
    manualSorting: true,
    getRowId,
    enableRowSelection: onRowSelectionChange
      ? canSelectRow
        ? (row) => canSelectRow(row.original)
        : true
      : false,
    state: {
      sorting: sorting ?? EMPTY_SORTING,
      rowSelection: rowSelection ?? EMPTY_ROW_SELECTION,
      columnPinning,
      columnOrder: tableColumnOrder,
      columnVisibility: tableColumnVisibility,
    },
    onSortingChange,
    onRowSelectionChange,
  });

  const rows = table.getRowModel().rows;
  const headerGroups = table.getHeaderGroups();

  // Track reactive state so renders refresh when these change
  const _rowSelection = table.getState().rowSelection;
  const _sorting = table.getState().sorting;
  void _rowSelection;
  void _sorting;
  void extraData;

  // -------------------------------------------------------------------------
  // Visual column order
  //
  // Header and body cells render in TanStack's pinning-partitioned order
  // (left → center → right), which diverges from getVisibleLeafColumns()
  // (pure columnOrder) once right-pinning and reorder exist. Every
  // per-column lookup is keyed by column id — never array index — and all
  // width/offset values live in the CSS variables the outer table maintains.
  // -------------------------------------------------------------------------

  const visualColumns = React.useMemo(
    () => headerGroups[0]?.headers.map((h) => h.column) ?? [],
    [headerGroups],
  );
  const visualColumnIds = visualColumns.map((c) => c.id).join(",");
  // The row's kebab rides in its leading content cell — the identity column,
  // on screen at every scroll position. A table whose columns are all
  // structural (no content cell to host it) falls back to the actions cell,
  // which then keeps its opaque lane fill instead of the edge fade.
  const kebabHostId = React.useMemo(() => {
    const first = visualColumns.find((c) => !STRUCTURAL_COLUMN_IDS.has(c.id));
    if (first) return first.id;
    return visualColumns.some((c) => c.id === "__actions__")
      ? "__actions__"
      : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- visualColumnIds is the identity of visualColumns' contents
  }, [visualColumnIds]);

  const {
    claimContextMenu,
    groupBodyRef,
    rowsContainerRef,
    virtualizer,
    virtualCount,
    headerSettling,
    getWindowRow,
    getRowAt,
    cacheVersion,
    pageSize: PAGE_SIZE,
  } = useDataTableGroupRows({
    group,
    rows,
    rowHeight,
    rowSelection,
    scrollContainerRef,
    isExpanded,
    isUngrouped,
    useInternalScroll,
    groupLoading,
    hadRows: hadRowsRef.current,
  });

  // -------------------------------------------------------------------------
  // Render: column headers (single pass — pinned columns get sticky style)
  // -------------------------------------------------------------------------

  const { isSelectAll, setSelectAll } = useDataTableSelectAll();

  // The group's first row, however the body is windowed — undefined until it
  // loads. When it is selected, the header draws the boundary line beneath
  // itself in the ACCENT: that boundary belongs to the selected row, but the
  // sticky header (opaque, z-20) paints over the row overlay's -1px tip, so
  // the row cannot draw it there itself (see ROW_SELECTED_CLASS).
  const firstBodyRow = virtualizer ? getWindowRow(0) : rows[0];
  const firstRowSelected =
    firstBodyRow != null &&
    (isSelectAll || !!rowSelection?.[firstBodyRow.id]);

  // Which chrome element sits directly above the group's first row decides
  // who repaints that shared boundary: a per-group column header renders
  // between band and rows (band → header → rows), but with `sharedHeader`
  // the one hoisted header has the group band BELOW it, and with
  // `hideHeader` there is no header at all — in both of those the band owns
  // the boundary. Exactly one of the two may paint the accent, or a selected
  // first row grows a second accent line one rung up.
  const headerAboveFirstRow = isUngrouped || (!sharedHeader && !hideHeader);
  const headerFirstRowSelected = firstRowSelected && headerAboveFirstRow;
  // Collapsed groups render the band over a zero-height body, so the
  // selected first row is not on screen and the band keeps its neutral line.
  const bandFirstRowSelected =
    firstRowSelected && isExpanded && !headerAboveFirstRow;

  // Grouped tables normally pin the group band at the top with the column
  // header just below it. With `sharedHeader` the order inverts: the single
  // hoisted column header owns the top edge and group bands pin under IT.
  const stickyColumnHeaderTop = stickyEnabled
    ? isUngrouped || sharedHeader
      ? 0
      : groupHeaderHeight
    : undefined;

  // The selection column is the leading structural lane every horizontal
  // line measures itself against — see the cellClasses recipe.
  const hasSelectColumn = rowSelection !== undefined;

  const renderHeader = React.useCallback(() => {
    return (
      <TableHeader
        // `[&_tr]:border-0` tw-merges away the stock `[&_tr]:border-b` (same
        // modifier chain) — the thead draws the header's bottom border itself,
        // and the arbitrary-variant selector would out-specify a plain
        // `border-0` on the row.
        className={cn(
          // The thead carries NO border of its own — the visible line rides
          // INSIDE the header row (see the tr below) so the pinned cells can
          // mask it, and a border strip here would open a 1px dead gap
          // between that line and the first body row.
          "block [&_tr]:border-0",
          SURFACE_BG[surface],
        )}
        style={
          stickyColumnHeaderTop !== undefined
            ? {
                position: "sticky" as const,
                top: stickyColumnHeaderTop,
                zIndex: 20,
              }
            : undefined
        }
      >
        {headerGroups.map((headerGroup, groupIndex) => {
          // The last consumer column gets no right resize handle in `fill`
          // layout: rows clamp to the container width, so shrinking that edge
          // could only open a gap the table immediately hides — a dead
          // affordance. Packed layout has no such clamp — the edge moves
          // freely — so every column keeps its handle.
          const lastResizableId =
            columnLayout === "packed"
              ? undefined
              : [...headerGroup.headers]
                  .reverse()
                  .find((h) => !STRUCTURAL_COLUMN_IDS.has(h.column.id))?.column
                  .id;
          // The row's leading content column — its header defaults to the
          // gradient hover (DataTableFirstHeaderProvider below).
          const firstContentId = headerGroup.headers.find(
            (h) => !STRUCTURAL_COLUMN_IDS.has(h.column.id),
          )?.column.id;
          return (
            <TableRow
              key={headerGroup.id}
              role="row"
              className={cn(
                "flex w-full border-0",
                ROW_STATES_OFF_CLASS,
                // Headers are drag/sort surfaces — text selection mid-gesture
                // reads as breakage.
                "select-none",
                "group/header",
                // The header's bottom line rides INSIDE the row (last px, no
                // z-index) so the pinned header cells' opaque fills (z-30)
                // mask it: the selection lane keeps its resting gap AND the
                // gap stays glued to the rail while columns scroll beneath —
                // a thead-level border sits below the cells' boxes, where
                // nothing can mask it, and scrolls with the content. Only
                // the bottom row of a stacked header draws it; the rows
                // above keep their inline group divider.
                !hideHeaderBorder &&
                  groupIndex === headerGroups.length - 1 &&
                  "after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-px",
                // Accent when the first row is selected — the same opaque mix
                // ROW_SELECTED_CLASS paints, so the selected block's top line
                // and its shared row lines are one color. Kept in sync by eye:
                // Tailwind only emits classes it can scan, so the mix cannot
                // be a shared constant interpolated into these strings.
                !hideHeaderBorder &&
                  groupIndex === headerGroups.length - 1 &&
                  (headerFirstRowSelected
                    ? "after:bg-[color-mix(in_oklab,var(--brand)_45%,var(--table-fill,var(--background)))]"
                    : "after:bg-border/80"),
              )}
              style={{
                height: densityConfig.headerHeight,
                ...TABLE_MIN_WIDTH_STYLE,
                // Divider between stacked header groups (rare: only column
                // grouping produces >1). Inline because the thead-level
                // `[&_tr]:border-0` above out-specifies row border classes.
                ...(groupIndex < headerGroups.length - 1
                  ? {
                      borderBottom:
                        "1px solid color-mix(in oklab, var(--border) 60%, transparent)",
                    }
                  : null),
              }}
            >
              {headerGroup.headers.map((header, headerIndex) => {
                const columnId = header.column.id;
                const meta = getColumnMeta(header.column);
                const isSelectionColumn = columnId === "select";
                const isPinned = !!header.column.getIsPinned();
                const isFirst = headerIndex === 0;
                const isLast = headerIndex === headerGroup.headers.length - 1;
                // Each lane's header cell that faces the scrolling columns —
                // the left lane's LAST, the right lane's FIRST — takes its
                // fill from the fade gradients instead, so an opaque
                // background-color here would sit under the gradient and
                // never let it dissolve.
                const isRightFadeCell =
                  header.column.getIsPinned() === "right" &&
                  headerGroup.headers[headerIndex - 1]?.column.getIsPinned() !==
                    "right";
                const isFadeCell =
                  isRightFadeCell ||
                  (header.column.getIsPinned() === "left" &&
                    headerGroup.headers[headerIndex + 1]?.column.getIsPinned() !==
                      "left");
                const canResize =
                  canResizeColumns &&
                  !STRUCTURAL_COLUMN_IDS.has(columnId) &&
                  columnId !== lastResizableId &&
                  header.column.columnDef.enableResizing !== false;
                const cell = (
                  <TableHead
                    key={header.id}
                    role="columnheader"
                    className={cn(
                      // `relative` anchors the absolutely-positioned resize
                      // handle.
                      "relative flex h-auto items-center whitespace-normal p-0",
                      HEADER_TEXT_CLASS,
                      "group/hcell",
                      getAlignmentClass(meta, isSelectionColumn),
                      getTruncationClass(meta),
                      isPinned && !isFadeCell && "bg-(--table-fill)",
                      isRightFadeCell && HEADER_FADE_RIGHT_CLASS,
                      // A pinned cell's opaque fill sits over the row's line
                      // pseudo, so it draws its own border — transparent on
                      // the leading structural lane (the resting gap), same
                      // as the body cells. Unpinned cells leave the line to
                      // the pseudo beneath them. A selected first row turns
                      // the whole line accent and FULL-BLEED — the selection
                      // lane included, matching the row overlay's span.
                      !hideHeaderBorder &&
                        groupIndex === headerGroups.length - 1 &&
                        isPinned &&
                        (headerFirstRowSelected
                          ? "border-b [border-color:color-mix(in_oklab,var(--brand)_45%,var(--table-fill,var(--background)))]"
                          : isFirst && STRUCTURAL_COLUMN_IDS.has(columnId)
                            ? "border-b border-transparent"
                            : "border-b border-border/80"),
                      draggingColumnId === columnId && "opacity-50",
                      // paddingXAlign picks what paddingX measures: 'edge'
                      // (default) is the raw inset outside the cell; 'content'
                      // density-compensates content columns so their CONTENT
                      // lands exactly paddingX from the table edge (structural
                      // select/actions columns take it raw either way).
                      paddingX &&
                        isFirst &&
                        (paddingXAlign !== "content" ||
                        STRUCTURAL_COLUMN_IDS.has(columnId)
                          ? BOX_PADDING_LEFT[paddingX]
                          : densityConfig.edgeGutterLeft[paddingX]),
                      paddingX &&
                        isLast &&
                        (paddingXAlign !== "content" ||
                        STRUCTURAL_COLUMN_IDS.has(columnId)
                          ? BOX_PADDING_RIGHT[paddingX]
                          : densityConfig.edgeGutterRight[paddingX]),
                    )}
                    style={headerCellStyles.get(columnId)}
                    data-dthcell="true"
                    // The column header's hover chrome keys off this while
                    // its seam is being dragged: pointer capture on the
                    // handle takes :hover off the cell, and the column being
                    // resized should keep reading as the one under the hand.
                    data-dtresizing={
                      activeResizeColumnId === columnId ? "true" : undefined
                    }
                    {...(reorderableIdSet.has(columnId)
                      ? { "data-dtreorderid": columnId }
                      : null)}
                  >
                    {header.isPlaceholder ? null : (
                      <DataTableFirstHeaderProvider
                        isFirst={columnId === firstContentId}
                      >
                        {(() => {
                          const content = flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          );
                          if (STRUCTURAL_COLUMN_IDS.has(header.column.id))
                            return content;
                          // Component-typed headers own their chrome — the
                          // documented idiom renders DataTableColumnHeader
                          // itself. flexRender types the element by the
                          // consumer's component (never DataTableColumnHeader
                          // directly), so wrapping again would nest two sort
                          // buttons and a click would toggle the sort twice.
                          // Only plain content (string headers) gets the
                          // automatic sort chrome.
                          if (
                            React.isValidElement(content) &&
                            typeof content.type !== "string"
                          ) {
                            return content;
                          }
                          return (
                            <DataTableColumnHeader
                              column={header.column}
                              title={content}
                            />
                          );
                        })()}
                      </DataTableFirstHeaderProvider>
                    )}
                    {/* Unmounted during a reorder drag — the boundary hover
                        lines and grabbable strip are resize affordances and
                        read as noise while a column is being moved. */}
                    {canResize && draggingColumnId === null ? (
                      <DataTableResizeHandle
                        columnId={columnId}
                        controller={resizeController}
                        active={activeResizeColumnId === columnId}
                      />
                    ) : null}
                  </TableHead>
                );
                // Structural columns get no header context menu at all. A
                // pinned consumer column keeps its SORT items — pinning locks
                // position, not sorting — but the move section gates on the
                // same set the drag-reorder uses, so the two move affordances
                // stay in lockstep, and the hide section follows the same
                // lock the column picker applies (pinned and
                // `enableHiding: false` columns are not hideable). Nothing in
                // any section = nothing to offer.
                if (STRUCTURAL_COLUMN_IDS.has(columnId)) return cell;
                const isReorderable = reorderableIdSet.has(columnId);
                const canSort = header.column.getCanSort();
                const canHide =
                  canHideColumns &&
                  isReorderable &&
                  header.column.columnDef.enableHiding !== false;
                if (
                  !isReorderable &&
                  !canSort &&
                  !canHide &&
                  renderColumnMenuItems === undefined
                )
                  return cell;
                const reorderIndex = reorderableIds.indexOf(columnId);
                const isFirstReorderable = reorderIndex <= 0;
                const isLastReorderable =
                  reorderIndex >= reorderableIds.length - 1;
                const sorted = header.column.getIsSorted();
                const move = (target: ColumnMoveTarget) => {
                  const result = computeColumnMove(
                    columnId,
                    reorderableIds,
                    target,
                  );
                  if (result) onColumnMove(columnId, result.beforeId);
                };
                const setSort = (dir: "asc" | "desc") => {
                  // Selecting the active direction clears the sort, matching
                  // the header press cycle's final step.
                  if (sorted === dir) header.column.clearSorting();
                  else header.column.toggleSorting(dir === "desc");
                };
                // Same shared-host treatment as rows: the header cell carries a
                // pointerdown claim, not a menu root of its own. One table has
                // a header row per group — otherwise N x columns more roots.
                // Sections in order: sort, consumer items, moves, hide — each
                // separated only from a non-empty predecessor. The consumer
                // thunk runs inside the render (menu open time), so a column
                // it answers null for composes as if the prop were absent.
                const renderColumnMenu = () => {
                  const consumerItems =
                    renderColumnMenuItems?.(columnId) ?? null;
                  return (
                  <>
                    {canSort ? (
                        <>
                          <ContextMenuItem onClick={() => setSort("asc")}>
                            <Icon
                              name={sorted === "asc" ? "check" : "arrow-up"}
                              size="lg"
                              color="muted"
                            />
                            <Text size="sm">Sort ascending</Text>
                          </ContextMenuItem>
                          <ContextMenuItem onClick={() => setSort("desc")}>
                            <Icon
                              name={sorted === "desc" ? "check" : "arrow-down"}
                              size="lg"
                              color="muted"
                            />
                            <Text size="sm">Sort descending</Text>
                          </ContextMenuItem>
                        </>
                      ) : null}
                      {consumerItems !== null ? (
                        <>
                          {canSort ? <ContextMenuSeparator /> : null}
                          {consumerItems}
                        </>
                      ) : null}
                      {isReorderable ? (
                        <>
                          {canSort || consumerItems !== null ? (
                            <ContextMenuSeparator />
                          ) : null}
                          <ContextMenuItem
                            disabled={isFirstReorderable}
                            onClick={() => move("left")}
                          >
                            <Icon name="arrow-left" size="lg" color="muted" />
                            <Text size="sm">Move Left</Text>
                          </ContextMenuItem>
                          <ContextMenuItem
                            disabled={isLastReorderable}
                            onClick={() => move("right")}
                          >
                            <Icon name="arrow-right" size="lg" color="muted" />
                            <Text size="sm">Move Right</Text>
                          </ContextMenuItem>
                          <ContextMenuItem
                            disabled={isFirstReorderable}
                            onClick={() => move("start")}
                          >
                            <Icon name="chevrons-left" size="lg" color="muted" />
                            <Text size="sm">Move to Start</Text>
                          </ContextMenuItem>
                          <ContextMenuItem
                            disabled={isLastReorderable}
                            onClick={() => move("end")}
                          >
                            <Icon
                              name="chevrons-right"
                              size="lg"
                              color="muted"
                            />
                            <Text size="sm">Move to End</Text>
                          </ContextMenuItem>
                        </>
                      ) : null}
                      {canHide ? (
                        <HideColumnMenuItem
                          columnId={columnId}
                          withSeparator={
                            canSort || consumerItems !== null || isReorderable
                          }
                        />
                      ) : null}
                  </>
                  );
                };
                if (!claimContextMenu) return cell;
                return React.cloneElement(cell, {
                  onPointerDown: claimContextMenu(renderColumnMenu),
                });
              })}
            </TableRow>
          );
        })}
      </TableHeader>
    );
    // The header cells render through `flexRender`, which reads
    // `column.getIsSorted()` when it is CALLED — inside this memo, where the
    // rule cannot follow it. `_sorting` is that read, hoisted so the memo
    // recomputes when the sort changes; drop it and the sort arrows freeze.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- _sorting is the hoisted read flexRender performs when called
  }, [
    headerGroups,
    densityConfig,
    headerCellStyles,
    _sorting,
    stickyColumnHeaderTop,
    hideHeaderBorder,
    headerFirstRowSelected,
    paddingX,
    paddingXAlign,
    canResizeColumns,
    columnLayout,
    resizeController,
    activeResizeColumnId,
    draggingColumnId,
    reorderableIdSet,
    reorderableIds,
    onColumnMove,
    renderColumnMenuItems,
    canHideColumns,
    claimContextMenu,
    surface,
  ]);

  // -------------------------------------------------------------------------
  // Pre-computed class strings for the render hot-path.
  // Eliminates hundreds of cn() (clsx + twMerge) calls per scroll frame
  // by computing them once when columns/density/padding change.
  // -------------------------------------------------------------------------

  const rowClasses = React.useMemo(() => {
    // Selected paint is driven by the `data-selected` attribute, so
    // one class string covers both states — the density text size rides on
    // the row and inherits into `size={null}` cell Text.
    const base = cn(
      "group/row relative flex w-full border-0",
      // The row owns its long press (it arms on hold): no text selection,
      // no iOS callout, no double-tap zoom — a cell that edits text is an
      // input, which states `select-text` itself.
      PRESS_SURFACE_CLASS,
      // Rows of a row-drag target group replay the hover wash (the pinned
      // cells replay `--row-wash`, so the tint runs edge to edge).
      "in-data-[drop-over]:[--row-wash:var(--wash-hover)]",
      // The row in flight dims in place; its lifted clone is what moves.
      "has-data-[row-drag-source]:opacity-40",
      densityConfig.textSize,
      SURFACE_BG[surface],
      ROW_HOVER_SOFT_CLASS,
      ROW_ARMED_CLASS,
      ROW_SELECTED_CLASS,
      ROW_SELECTED_HOVER_CLASS,
      ROW_BUTTON_SHAPE_CLASS,
    );
    return {
      base,
      clickable: cn(base, "cursor-pointer"),
    };
  }, [densityConfig.textSize, surface]);

  const cellClasses = React.useMemo(() => {
    const map = new Map<string, { regular: string; last: string }>();
    visualColumns.forEach((col, cellIndex) => {
      const meta = getColumnMeta(col);
      const isPinned = !!col.getIsPinned();
      const isFirst = cellIndex === 0;
      const isLast = cellIndex === visualColumns.length - 1;
      const base = cn(
        "relative flex items-stretch whitespace-normal p-0",
        isPinned ? "z-10" : "z-0",
        getTruncationClass(meta),
        // Same edge/content rule as the header cells above.
        paddingX &&
          isFirst &&
          (paddingXAlign !== "content" || STRUCTURAL_COLUMN_IDS.has(col.id)
            ? BOX_PADDING_LEFT[paddingX]
            : densityConfig.edgeGutterLeft[paddingX]),
        paddingX &&
          isLast &&
          (paddingXAlign !== "content" || STRUCTURAL_COLUMN_IDS.has(col.id)
            ? BOX_PADDING_RIGHT[paddingX]
            : densityConfig.edgeGutterRight[paddingX]),
      );
      // The right lane's cell is the body's edge fade (BODY_EDGE_FADE_CLASS)
      // unless it is hosting the kebab for a content-less table, in which
      // case it keeps the opaque lane paint like the left lane.
      const isEdgeFade =
        col.getIsPinned() === "right" && kebabHostId !== col.id;
      const pinned = isEdgeFade
        ? cn(base, BODY_EDGE_FADE_CLASS)
        : isPinned
          ? cn(base, "bg-(--table-fill)", PINNED_CELL_WASH_CLASS)
          : base;
      map.set(col.id, {
        // The leading structural lane (the selection column) sits OUTSIDE
        // the resting separator: its border is transparent, so the line
        // starts where the content lane begins. Its opaque pinned fill then
        // masks content borders scrolling beneath it, keeping the gap glued
        // to the rail. Tables with no selection column have no lane, and
        // their lines run the full width. State washes stay full-bleed
        // either way — they ride the row background, not the borders.
        regular: cn(
          pinned,
          "border-b",
          isFirst && STRUCTURAL_COLUMN_IDS.has(col.id)
            ? "border-transparent"
            : "border-border/60",
        ),
        last: pinned,
      });
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the visual column ids; the column objects are derived from them
  }, [visualColumnIds, kebabHostId, densityConfig, paddingX, paddingXAlign]);

  // -------------------------------------------------------------------------
  // Render: single row (single pass — pinned cells get sticky style)
  // -------------------------------------------------------------------------

  // Pre-computed row style — shared by all rows; virtual rows merge their
  // absolute positioning on top.
  const rowStyle = React.useMemo<React.CSSProperties>(
    () => ({ height: densityConfig.rowHeight, ...TABLE_MIN_WIDTH_STYLE }),
    [densityConfig.rowHeight],
  );

  const isEmpty =
    !groupLoading &&
    rows.length === 0 &&
    virtualCount === 0 &&
    !group.totalCount;

  // A group footer ("+ Add …") sits flush under the last data row — keep
  // that row's bottom divider so the footer reads as chrome, not another
  // row. The footer is also what keeps an EMPTY group live: with one, the
  // group expands to offer just the footer row; without one (the consumer
  // returned null — e.g. the viewer can't create) an empty group is an
  // inert collapsed reference and its body renders nothing. A skeleton
  // stand-in never calls the consumer: its key names no real group.
  const groupFooterNode =
    groupFooter && !group.skeleton ? groupFooter(groupKey) : null;
  const hasFooter = groupFooterNode !== null;
  const keepLastRowBorder = showLastRowBorder || hasFooter;
  // Grouped empty groups never render the empty state (the band + optional
  // footer IS the empty presentation); the ungrouped table keeps it. A
  // placeholder group is lazy, not empty — it must keep its PlaceholderBody
  // so visibility can activate it.
  const inertEmpty =
    isEmpty && !isUngrouped && !hasFooter && !group.placeholder;

  const renderRow = React.useCallback(
    (
      row: Row<TData>,
      index: number,
      groupRowCount: number,
      virtualStyle?: React.CSSProperties,
    ) => {
      const visibleCells = row.getVisibleCells();
      const isClickable =
        !!onRowClick && (rowClickable ? rowClickable(row) : true);
      const isSelected = row.getIsSelected() || isSelectAll;
      const isLastRow = index === groupRowCount - 1 && !keepLastRowBorder;

      const rowElement = (
        <TableRow
          // Namespaced because a loaded row and an unloaded slot share ONE key
          // space. A skeleton is keyed by its virtual INDEX, and React
          // stringifies keys, so a bare `row.id` of "1" collides with slot 1 —
          // which is not a corner case but the ordinary server-paginated
          // window, where ids and indices both start counting at the top.
          // React's answer to a duplicate key is to reuse one child's DOM for
          // the other or drop it, so a skeleton keeps the row's place.
          key={`row-${row.id}`}
          role="row"
          // The row's index in its group: row-drag slot geometry orders the
          // rendered rows by it, since the virtualizer positions them
          // absolutely.
          data-row-index={index}
          // The row's identity for the drop settle's landing query.
          data-row-id={row.id}
          aria-selected={isSelected}
          data-selected={isSelected ? "" : undefined}
          className={isClickable ? rowClasses.clickable : rowClasses.base}
          style={virtualStyle ? { ...rowStyle, ...virtualStyle } : rowStyle}
          // A finger held anywhere in the row arms it (see ROW_ARMED_CLASS)
          // and marks the press spent; the cells' own handlers ask
          // `pressArmed` and treat that release as the reveal, not a press.
          onPointerDownCapture={
            armRowsOnHold
              ? (e: React.PointerEvent<HTMLTableRowElement>) => {
                  beginHold(e.nativeEvent, e.currentTarget);
                }
              : undefined
          }
          // Only a clickable row carries the handler: `TableRow` reads
          // `onClick` as "this row is the press target" and answers with the
          // pointer cursor, so an inert row must not carry one. The table's
          // keyboard model routes through its cells, not its rows, so a
          // clickable row is not a tab stop either.
          onClick={
            isClickable
              ? (e: React.MouseEvent<HTMLTableRowElement>) => {
                  if (!isOwnPress(e.currentTarget, e.target)) return;
                  if (pressArmed(e.currentTarget)) return;
                  onRowClick(row);
                }
              : undefined
          }
          tabIndex={isClickable ? -1 : undefined}
          onPointerDown={
            renderRowActions && claimContextMenu
              ? claimContextMenu(() => renderRowActions(row.original))
              : undefined
          }
        >
          {visibleCells.map((cell) => {
            const precomputed = cellClasses.get(cell.column.id);
            const content = (
              <DataTableCellSlot
                render={cell.column.columnDef.cell}
                ctx={cell.getContext()}
              />
            );
            const rowActions =
              renderRowActions && cell.column.id === kebabHostId
                ? renderRowActions(row.original)
                : null;
            return (
              <TableCell
                key={cell.id}
                role="cell"
                className={isLastRow ? precomputed?.last : precomputed?.regular}
                style={bodyCellStyles.get(cell.column.id)}
              >
                {rowActions ? (
                  // The host cell's own content keeps the WHOLE width; the
                  // kebab is offered to the content's hover cluster (which
                  // adopts it as its last action) or floats over the
                  // trailing edge on its own, reserving nothing either way.
                  <DataTableRowKebabHost actions={rowActions}>
                    {content}
                  </DataTableRowKebabHost>
                ) : (
                  content
                )}
              </TableCell>
            );
          })}
          <td
            aria-hidden="true"
            className={isLastRow ? ROW_FILLER_LAST_CLASS : ROW_FILLER_CLASS}
          />
        </TableRow>
      );

      return rowElement;
    },
    [
      onRowClick,
      rowClickable,
      isSelectAll,
      keepLastRowBorder,
      rowStyle,
      bodyCellStyles,
      kebabHostId,
      rowClasses,
      cellClasses,
      renderRowActions,
      armRowsOnHold,
      claimContextMenu,
    ],
  );

  // -------------------------------------------------------------------------
  // Render: virtualized rows (single pass — no pane splitting)
  // -------------------------------------------------------------------------

  // Rendered for a windowed slot whose row has not loaded (immediate — the
  // slot is already on screen mid-scroll) and for the initial-load rows
  // (`delayed` — invisible for their first ~200ms so a fast query or warm
  // cache never flashes placeholders).
  const renderSkeletonRow = React.useCallback(
    (
      index: number,
      total: number,
      virtualStyle: React.CSSProperties,
      key: React.Key,
      delayed?: boolean,
    ) => {
      const isLastRow = index === total - 1;
      return (
        <TableRow
          // The other half of the namespace `renderRow` opens: `slot-` can
          // never equal `row-`, so no row id can collide with a virtual index
          // however the two are numbered.
          key={`slot-${key}`}
          role="row"
          className={rowClasses.base}
          style={{ ...rowStyle, ...virtualStyle }}
        >
          {visualColumns.map((col, idx) => {
            const precomputed = cellClasses.get(col.id);
            return (
              <TableCell
                key={col.id}
                role="cell"
                className={isLastRow ? precomputed?.last : precomputed?.regular}
                style={bodyCellStyles.get(col.id)}
              >
                <DataTableCellContent>
                  {idx === 0 && density === "comfortable" ? (
                    // The comfortable rung exists for a stacked identity
                    // cell, so its first-column stand-in is two bars: a
                    // skeleton mirrors the settled page, and a single bar
                    // in a 52px row would predict a page that never comes.
                    <Box direction="column" gap="xs" fill="width">
                      <Skeleton delayed={delayed} className="h-3 w-3/4 rounded" />
                      <Skeleton delayed={delayed} className="h-2.5 w-1/3 rounded" />
                    </Box>
                  ) : (
                    <Skeleton
                      delayed={delayed}
                      className={
                        idx === 0 ? "h-3 w-3/4 rounded" : "h-3 w-1/2 rounded"
                      }
                    />
                  )}
                </DataTableCellContent>
              </TableCell>
            );
          })}
        </TableRow>
      );
    },
    [rowClasses, rowStyle, visualColumns, cellClasses, bodyCellStyles, density],
  );

  const renderVirtualRows = React.useCallback(
    () => {
      if (!virtualizer) {
        // Non-virtualized fallback (virtualizer disabled — external/fit)
        return rows.map((row, index) => renderRow(row, index, rows.length));
      }

      const items = virtualizer.getVirtualItems();
      const margin = virtualizer.options.scrollMargin;

      // The tbody itself is the sized positioning context (block + relative +
      // height: totalSize) — each virtual row is an absolutely positioned <tr>
      // translated to its slot, so no non-table wrapper enters the table.
      return items.map((virtualItem) => {
        const row = getWindowRow(virtualItem.index);
        const virtualStyle: React.CSSProperties = {
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: virtualItem.size,
          transform: `translateY(${virtualItem.start - margin}px)`,
        };
        return row
          ? renderRow(row, virtualItem.index, virtualCount, virtualStyle)
          : renderSkeletonRow(
              virtualItem.index,
              virtualCount,
              virtualStyle,
              virtualItem.key,
            );
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cacheVersion stands for the page cache ref contents
    [
      virtualizer,
      rows,
      renderRow,
      renderSkeletonRow,
      virtualCount,
      getWindowRow,
      cacheVersion,
    ],
  );

  // -------------------------------------------------------------------------
  // Group header with access to per-group rows
  // -------------------------------------------------------------------------

  const selectAll = React.useCallback(() => {
    table.setRowSelection((old) => {
      const next = { ...old };
      for (const row of rows) {
        if (row.getCanSelect()) next[row.id] = true;
      }
      return next;
    });
    setSelectAll(true);
  }, [table, rows, setSelectAll]);

  const deselectAll = React.useCallback(() => {
    for (const row of rows) {
      row.toggleSelected(false);
    }
    // Clearing the rows is not enough: `isSelectAll` also paints every row
    // selected (it covers rows evicted with their page) and keeps the group in
    // the bulk bar's `selectAllGroups`. Leaving it armed left a "deselected"
    // group still washed selected, still counted, and with nothing checked —
    // the same pairing the header checkbox and the per-row cell already make.
    setSelectAll(false);
  }, [rows, setSelectAll]);

  // Only selectable rows count toward "group fully selected" — rows the
  // viewer can't act on have no checkbox and can never be selected.
  const selectableRows = rows.filter((r) => r.getCanSelect());
  const isGroupSelected =
    selectableRows.length > 0 && selectableRows.every((r) => r.getIsSelected());

  // Bind the key-accepting onToggle to this group's key for child components
  // that expect () => void.  Stable as long as onToggle + groupKey are stable.
  const handleToggle = React.useCallback(
    () => onToggle(groupKey),
    [onToggle, groupKey],
  );

  const groupCount = group.totalCount ?? group.data.length;
  const groupHeader = React.useMemo(
    () => (
      <DataTableGroupHeader
        isExpanded={isExpanded}
        onToggle={handleToggle}
        groupLabel={group.skeleton ? undefined : group.label}
        // A skeleton stand-in's key names no real group, so the consumer's
        // title renderer never sees it — a delayed bar holds the title slot,
        // width staggered by position so the mirror reads like varied labels.
        title={
          group.skeleton ? (
            <Skeleton delayed height={12} vary={groupIndex} width={96} />
          ) : groupTitle ? (
            groupTitle(groupKey)
          ) : (
            group.label
          )
        }
        count={hideGroupCount || group.skeleton ? undefined : groupCount}
        // A skeleton stand-in's key names no real group, so it never asks.
        interactiveTitle={
          !group.skeleton && (groupTitleInteractive?.(groupKey) ?? false)
        }
        // An empty group stays togglable while it has a footer to reveal;
        // only footerless empty groups are inert.
        disabled={groupCount === 0 && !hasFooter}
        leadingWidth={groupLeading?.width}
        chevronMargin={groupLeading?.chevronMargin}
        actionsOnGroupHover
        className={cn(
          paddingX && BOX_PADDING_X[paddingX],
          // Drop target of a row drag: the band tints brand while a row from
          // another group hovers the group (`data-drop-over` on the scope box).
          "in-data-[drop-over]:bg-brand/10",
          // Same lane rule as the column header line above. When the group's
          // first row is selected the band owns the shared boundary for the
          // same reason the column header does — the band's opaque sticky
          // fill (z-30) covers the row overlay's -1px tip — so the line goes
          // accent and FULL-BLEED, matching the row overlay's span.
          groupHeaderBorder &&
            (bandFirstRowSelected
              ? "relative border-b border-transparent after:pointer-events-none after:absolute after:inset-x-0 after:-bottom-px after:h-px after:bg-[color-mix(in_oklab,var(--brand)_45%,var(--table-fill,var(--background)))]"
              : hasSelectColumn
                ? "relative border-b border-transparent after:pointer-events-none after:absolute after:left-11 after:right-0 after:-bottom-px after:h-px after:bg-border/60"
                : "border-b border-border/60"),
        )}
        // Empty groups keep their actions: the cluster carries group-scoped
        // commands (quick-add, consumer-appended menu items) that must stay
        // reachable when the group has no rows. Skeleton stand-ins carry
        // none — their key names no real group to act on.
        actions={
          group.skeleton
            ? undefined
            : groupActions?.({
                groupKey,
                rows,
                isCollapsed: !isExpanded,
                isGroupSelected,
                collapseGroup: handleToggle,
                collapseAll,
                expandAll,
                allCollapsed,
                selectAll,
                deselectAll,
              })
        }
      />
    ),
    [
      isExpanded,
      handleToggle,
      groupTitle,
      groupTitleInteractive,
      groupKey,
      group.label,
      group.skeleton,
      groupIndex,
      groupCount,
      hideGroupCount,
      paddingX,
      groupLeading,
      groupHeaderBorder,
      hasSelectColumn,
      bandFirstRowSelected,
      groupActions,
      rows,
      collapseAll,
      expandAll,
      allCollapsed,
      selectAll,
      deselectAll,
      isGroupSelected,
      hasFooter,
    ],
  );

  // -------------------------------------------------------------------------
  // Render: group body content
  // -------------------------------------------------------------------------

  // When loading with a known total, reserve totalCount * rowHeight so the
  // scroll area is correctly sized and fetchPage can trigger when the group
  // scrolls into the viewport. The first row-slots of that space render as
  // skeleton rows (the band's label and count are already real, so the group
  // reads as filling in, not blank) — delayed on a first paint, immediate on a
  // re-load; the spacer below them keeps the reserved height exact.
  const loadingPlaceholderHeight =
    groupLoading && virtualCount > 0 ? virtualCount * rowHeight : 0;
  // How many of those slots carry a skeleton: every slot the viewport SHOWS,
  // with the capped run as the floor. The virtualizer already knows that run —
  // it is sized to the group's total and measures the scroll box — so a group
  // of six paints six bars, not five over an empty sixth slot, and a re-load
  // replaces the rows that were on screen edge to edge rather than with five
  // bars over a void. Slots below the fold stay bare: the reserved height
  // alone says "more here". Before the first measure the window is empty and
  // the floor holds; the first-paint run is delayed, so that frame is unseen.
  const viewportSlots = virtualizer ? virtualizer.getVirtualItems().length : 0;
  const placeholderRows = Math.max(INITIAL_LOADING_ROW_COUNT, viewportSlots);
  const loadingPlaceholderRowCount =
    loadingPlaceholderHeight > 0 ? Math.min(virtualCount, placeholderRows) : 0;

  const emptyContent = emptyState ?? <DataTableEmpty />;

  // What the footer's content ("+ label") needs to land on the first
  // column's text line. With a selection column that line is
  // SELECTION_COLUMN_WIDTH + the first cell's own left pad from the table
  // edge, full stop — the select column is a fixed 44px box that absorbs
  // paddingX inside itself. Without one, content sits at paddingX + the
  // first cell's own pad.
  const footerChrome = React.useMemo(
    () => ({
      contentLeadingPx:
        (hasSelectColumn
          ? SELECTION_COLUMN_WIDTH
          : paddingX
            ? PADDING_X_PX[paddingX]
            : 0) + densityConfig.cellPadLeftPx,
    }),
    [hasSelectColumn, paddingX, densityConfig],
  );

  // See the render site for the z rationale.
  const footerPinnedBandStyle = React.useMemo<
    React.CSSProperties | undefined
  >(
    () =>
      containerWidth > 0
        ? {
            position: "sticky" as const,
            left: 0,
            width: bandWidth,
            zIndex: 10,
          }
        : undefined,
    [containerWidth, bandWidth],
  );

  // -------------------------------------------------------------------------
  // Sticky group header: pinned both vertically and horizontally
  // -------------------------------------------------------------------------

  // Measured via ResizeObserver — the height feeds the column header's
  // sticky `top`.
  const [groupHeaderEl, setGroupHeaderEl] = React.useState<HTMLElement | null>(
    null,
  );

  React.useEffect(() => {
    if (!groupHeaderEl) return;
    const measure = () => {
      const nextHeight = groupHeaderEl.getBoundingClientRect().height;
      setGroupHeaderHeight((current) =>
        current === nextHeight ? current : nextHeight,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(groupHeaderEl);
    return () => observer.disconnect();
  }, [groupHeaderEl]);

  // With `sharedHeader` the hoisted column header pins at top: 0, so group
  // bands pin just below it — the thead is exactly the header row (the
  // bottom line paints inside that row; the thead carries no border).
  const stickyGroupHeaderTop =
    sharedHeader && !hideHeader
      ? densityConfig.headerHeight
      : 0;
  const stickyGroupHeaderStyle = React.useMemo<React.CSSProperties>(
    () => ({
      position: "sticky",
      top: stickyGroupHeaderTop,
      left: 0,
      width: bandWidth,
      zIndex: 30,
    }),
    [bandWidth, stickyGroupHeaderTop],
  );

  // Horizontal-only pin for the non-`stickyHeader` path: the title band still
  // holds the scrollport's left edge while the columns pan under it (reference
  // parity) without opting into vertical stickiness. Skipped until the
  // container is measured — a `width: 0` band would flash empty on first paint.
  const horizontalPinnedBandStyle = React.useMemo<
    React.CSSProperties | undefined
  >(
    () =>
      containerWidth > 0
        ? { position: "sticky", left: 0, width: bandWidth, zIndex: 20 }
        : undefined,
    [containerWidth, bandWidth],
  );

  // Hands the sticky header's node to the height measurement effect above.
  const headerNodeRefCb = React.useCallback((node: HTMLDivElement | null) => {
    setGroupHeaderEl(node);
  }, []);

  const renderGroupHeader = React.useCallback(() => {
    if (isUngrouped) return null;

    if (stickyEnabled) {
      return (
        <Box
          ref={headerNodeRefCb}
          className={SURFACE_BG[surface]}
          data-slot="table-group-band"
          style={stickyGroupHeaderStyle}
        >
          {groupHeader}
        </Box>
      );
    }

    return (
      <Box
        className={SURFACE_BG[surface]}
        data-slot="table-group-band"
        style={horizontalPinnedBandStyle}
      >
        {groupHeader}
      </Box>
    );
  }, [
    isUngrouped,
    stickyEnabled,
    headerNodeRefCb,
    stickyGroupHeaderStyle,
    horizontalPinnedBandStyle,
    groupHeader,
    surface,
  ]);

  // Single top header (first group only), hoisted ABOVE the group band and
  // OUTSIDE the group's hover-scope box: that box is its descendants' sticky
  // containing block, so a header inside it would un-stick as the first
  // group scrolls past. Rendered in EVERY return path so collapsing the
  // first group doesn't take the shared column header with it; suppressed
  // when the group is empty so the empty state stands alone. It needs its
  // own <table> shell (the group bodies each own one), and the SHELL carries
  // the sticky/z styling — the thead's own sticky is inert inside a wrapper
  // exactly its height, and so is the <table>'s: `Table` spreads `style` onto
  // the inner <table>, so the sticky must ride the `render` Box (the shell)
  // or it is clamped inside a box its own height and never pins.
  const topHeader =
    sharedHeader &&
    groupIndex === 0 &&
    !hideHeader &&
    !headerSettling &&
    (!isEmpty || hasFooter) ? (
      <Table
        variant="default"
        render={
          <Box
            overflow="visible"
            style={
              stickyEnabled
                ? {
                    ...TABLE_MIN_WIDTH_STYLE,
                    position: "sticky",
                    top: 0,
                    // Above the group bands (z 30): the shared header owns
                    // the top edge, so an outgoing band pushed off by the
                    // next group slides UNDER it, never across it. Below the
                    // bulk bar (z 40).
                    zIndex: 35,
                  }
                : TABLE_MIN_WIDTH_STYLE
            }
          />
        }
        role="table"
        className="block"
        style={TABLE_MIN_WIDTH_STYLE}
      >
        {renderHeader()}
      </Table>
    ) : null;

  // Trailing gap between groups — static classes (Tailwind can't emit
  // dynamic margin values), keyed by the groupGap prop. Applied to expanded
  // bodies AND collapsed placeholders so collapsed groups keep the same
  // breathing room.
  // Horizontally pinned like the group title band — a sideways pan must never
  // carry "+ Add …" off-screen. Its OWN z tier: it scrolls vertically like a
  // row, so it must slide UNDER the sticky column header (z 20) and group
  // bands (z 30) yet above plain cells (10). Shown from the first paint of a
  // group with a known size, so it never arrives late and shoves the groups
  // below it down.
  const footerElement = groupFooterNode ? (
    <Box style={footerPinnedBandStyle}>
      <DataTableFooterChromeProvider value={footerChrome}>
        {groupFooterNode}
      </DataTableFooterChromeProvider>
    </Box>
  ) : null;

  const groupGapClass = !isUngrouped
    ? groupGap === "xl"
      ? "mb-10"
      : groupGap === "lg"
        ? "mb-6"
        : "mb-4"
    : undefined;

  if (!isExpanded || inertEmpty) {
    // Collapsed: render header + zero-height body placeholder to keep hooks
    // consistent. The placeholder still carries the group gap — collapsed
    // groups keep the same breathing room as expanded ones, so collapsing
    // one doesn't slam the next header against it. An inert empty group
    // (no footer to reveal) renders this way regardless of stored expand
    // state — its band already reads collapsed, so its body shows nothing.
    return (
      <>
        {topHeader}
        <GroupScopeBox
          enabled={!isUngrouped}
          groupKey={groupKey}
          dropTarget={rowDragEnabled}
        >
          {renderGroupHeader()}
          <Box
            ref={groupBodyRef}
            className={groupGapClass}
            style={{ height: 0, overflow: "hidden" }}
          />
        </GroupScopeBox>
      </>
    );
  }

  // A zero-count placeholder reserves no scroll space, so its visibility
  // sentinel never fires and there is nothing to fetch anyway — with a
  // footer it renders as a live empty group (column header + "+ Add …" row)
  // by falling through to the body below instead of an empty band.
  if (group.placeholder && !(isEmpty && hasFooter)) {
    // Placeholder: render a fixed-height empty view sized to totalCount × rowHeight.
    // No virtualizer, no data fetching — just reserves scroll space.
    // When the placeholder scrolls into view, call fetchPage to activate it.
    const placeholderCount = typeof group.totalCount === "number" ? group.totalCount : 0;
    // Drawn as the loading body it becomes (column header over skeleton rows),
    // so activation swaps in nothing new: a bare box here blanks the group
    // for the frames before its visibility sentinel fires.
    const placeholderHeader = placeholderCount > 0 && !hideHeader && !sharedHeader;
    const placeholderHeight =
      placeholderCount * rowHeight + (placeholderHeader ? densityConfig.headerHeight : 0);
    const placeholderSkeletonRows = Math.min(placeholderCount, INITIAL_LOADING_ROW_COUNT);
    return (
      <>
        {topHeader}
        <GroupScopeBox
          enabled={!isUngrouped}
          groupKey={groupKey}
          dropTarget={rowDragEnabled}
        >
          {renderGroupHeader()}
          <PlaceholderBody
            ref={groupBodyRef}
            className={groupGapClass}
            height={placeholderHeight}
            footer={placeholderCount > 0 ? footerElement : null}
            onVisible={
              group.fetchPage ? () => group.fetchPage!(0, PAGE_SIZE) : undefined
            }
          >
            {placeholderCount > 0 ? (
              <Table
                variant="default"
                render={<Box overflow="visible" />}
                role="table"
                className="block"
                style={TABLE_MIN_WIDTH_STYLE}
              >
                {placeholderHeader ? renderHeader() : null}
                <TableBody className="relative block w-full">
                  {Array.from({ length: placeholderSkeletonRows }, (_, index) =>
                    renderSkeletonRow(index, placeholderSkeletonRows, {}, `loading-${index}`, true),
                  )}
                </TableBody>
              </Table>
            ) : null}
          </PlaceholderBody>
        </GroupScopeBox>
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Body: real table markup, display-overridden. The outer Box owns all
  // scrolling — the base Table's overflow container is collapsed via its
  // render prop, and header/body flow directly in the shared scroll context
  // (sticky headers need unobstructed access to the scroll ancestor).
  // Empty and loading states are NOT table content, so they render as
  // siblings of the <table>, never inside it.
  // -------------------------------------------------------------------------

  // With `sharedHeader` the header is hoisted above the first group
  // (topHeader), so the in-body copy is suppressed here.
  const showHeader =
    !hideHeader && !sharedHeader && !headerSettling && (!isEmpty || !isUngrouped);
  const showRows = !isEmpty && loadingPlaceholderHeight === 0;
  // The very first load, before anything is known — no rows and no total
  // (a known total renders the sized placeholder above instead). A capped
  // run of skeleton rows stands in for the list — DELAYED on a first paint,
  // so a warm cache or fast query swaps real rows in before any shimmer
  // paints, and immediate when the group showed rows before (see `reloading`).
  const showInitialLoadingRows =
    showRows && groupLoading && rows.length === 0 && virtualCount === 0;

  const bodyContent = (
    <Box
      ref={groupBodyRef}
      className={cn(
        groupGapClass,
        // The empty state stretches to fill the table, which needs an
        // unbroken `flex-1` chain — declared explicitly on this branch only.
        isEmpty && isUngrouped ? "flex flex-1 flex-col" : undefined,
      )}
    >
      <Box
        className={isEmpty && isUngrouped ? "flex flex-1 flex-col" : undefined}
        style={isEmpty ? undefined : TABLE_MIN_WIDTH_STYLE}
      >
        {showHeader || showRows || loadingPlaceholderRowCount > 0 ? (
          <Table
            variant="default"
            render={<Box overflow="visible" />}
            role="table"
            className="block"
            style={TABLE_MIN_WIDTH_STYLE}
          >
            {showHeader ? renderHeader() : null}
            {showRows ? (
              <TableBody
                ref={rowsContainerRef}
                className="relative block w-full"
                style={
                  // The loading rows FLOW — the virtualizer knows 0 rows, and
                  // its 0px total would collapse the tbody over them.
                  virtualizer && !showInitialLoadingRows
                    ? { height: virtualizer.getTotalSize() }
                    : undefined
                }
              >
                {showInitialLoadingRows
                  ? Array.from(
                      { length: INITIAL_LOADING_ROW_COUNT },
                      (_, index) =>
                        renderSkeletonRow(
                          index,
                          INITIAL_LOADING_ROW_COUNT,
                          {},
                          `loading-${index}`,
                          !reloading,
                        ),
                    )
                  : renderVirtualRows()}
              </TableBody>
            ) : loadingPlaceholderRowCount > 0 ? (
              // The sized-placeholder path: the first row-slots of the
              // reserved space carry skeleton rows; the spacer Box below the
              // table makes up the rest of the reserved height.
              <TableBody className="relative block w-full">
                {Array.from(
                  { length: loadingPlaceholderRowCount },
                  (_, index) =>
                    renderSkeletonRow(
                      index,
                      loadingPlaceholderRowCount,
                      {},
                      `loading-${index}`,
                      !reloading,
                    ),
                )}
              </TableBody>
            ) : null}
          </Table>
        ) : null}
        {(showRows || (isEmpty && !isUngrouped)) ? footerElement : null}
        {isEmpty && isUngrouped ? (
          emptyContent
        ) : loadingPlaceholderHeight > 0 ? (
          <Box
            style={{
              height:
                loadingPlaceholderHeight -
                loadingPlaceholderRowCount * rowHeight,
            }}
          />
        ) : null}
        {/* Under the reserved rows while they load, where it settles. */}
        {!showRows && loadingPlaceholderHeight > 0 ? footerElement : null}
      </Box>
    </Box>
  );


  return (
    <DataTableShiftSelectProvider>
      <DataTableGroupRowsProvider knownEmpty={isEmpty} rows={rows}>
        <DataTableRowDragGroupProvider
          groupKey={groupKey}
          getRowAt={getRowAt}
          total={virtualCount}
        >
          {topHeader}
          <GroupScopeBox
            enabled={!isUngrouped}
            groupKey={groupKey}
            dropTarget={rowDragEnabled}
          >
            {renderGroupHeader()}
            {bodyContent}
          </GroupScopeBox>
        </DataTableRowDragGroupProvider>
      </DataTableGroupRowsProvider>
    </DataTableShiftSelectProvider>
  );
}

DataTableGroupContentInner.displayName = "DataTableGroupContent";

// Memoize to prevent re-rendering groups whose data hasn't changed.
// The `group` prop is always a new object (from the parent's useMemo), so we
// shallow-compare its individual fields instead of using default reference equality.
function groupPropsAreEqual<TData>(
  prev: DataTableGroupContentProps<TData>,
  next: DataTableGroupContentProps<TData>,
): boolean {
  if (prev.group !== next.group) {
    const pg = prev.group;
    const ng = next.group;
    if (
      pg.data !== ng.data ||
      pg.totalCount !== ng.totalCount ||
      pg.dataOffset !== ng.dataOffset ||
      pg.loading !== ng.loading ||
      pg.placeholder !== ng.placeholder ||
      pg.skeleton !== ng.skeleton ||
      pg.fetchPage !== ng.fetchPage ||
      pg.label !== ng.label ||
      pg.key !== ng.key
    )
      return false;
  }
  // Shallow-compare all other props
  const keys = Object.keys(next) as (keyof DataTableGroupContentProps<TData>)[];
  for (const key of keys) {
    if (key === "group") continue;
    if (prev[key] !== next[key]) return false;
  }
  return true;
}

const DataTableGroupContent = React.memo(
  DataTableGroupContentInner,
  groupPropsAreEqual,
) as typeof DataTableGroupContentInner;

// ---------------------------------------------------------------------------
// DataTable — main component
// ---------------------------------------------------------------------------

/**
 * DataTable — a grouped, virtualized data table component.
 *
 * Renders each group as an independent virtualized sub-table with its own
 * TanStack Table instance and virtualizer.  When `totalCount` exceeds loaded
 * `data.length`, the virtualizer sizes the scroll area to the total and
 * renders skeleton rows for unloaded indices.  Scrolling to an unloaded
 * region calls `fetchPage(offset, limit)` (debounced 300ms) so the provider
 * can re-fetch at that position.
 *
 * Ungrouped = one group. Grouped = N groups. Same code path.
 *
 * @example
 * ```tsx
 * // Ungrouped
 * <DataTable
 *   columns={columns}
 *   groups={[{ key: '__all__', label: 'All', data: members, totalCount: 200, fetchPage }]}
 * />
 *
 * // Grouped
 * <DataTable
 *   columns={columns}
 *   groups={[
 *     { key: 'team-a', label: 'Team A', data: teamAData, totalCount: 80, fetchPage: fetchA },
 *     { key: 'team-b', label: 'Team B', data: teamBData, totalCount: 45, fetchPage: fetchB },
 *   ]}
 *   groupTitle={(key) => <TeamLabel name={key} />}
 * />
 * ```
 */
export function DataTable<TData>({
  columns: columnsProp,
  groups,
  surface = "background",
  getRowId,
  sorting,
  onSortingChange,
  columnOrder: columnOrderProp,
  onColumnOrderChange,
  columnVisibility: columnVisibilityProp,
  onColumnVisibilityChange,
  addColumnHeader,
  columnSizing: columnSizingProp,
  onColumnSizingChange,
  extraData,
  pinnedColumnIds = DEFAULT_PINNED_COLUMN_IDS,
  fillColumnId,
  columnLayout = "packed",
  stickyHeader = false,
  scrollFade = false,
  scrollMode = "internal",
  hideHeader = false,
  sharedHeader = false,
  borders = "rows",
  groupGap = "md",
  density = "regular",
  loading = false,
  emptyState,
  onRowClick,
  rowClickable,
  renderRowActions: renderRowActionsProp,
  renderColumnMenuItems,
  "data-testid": dataTestId,
  groupTitle,
  groupTitleInteractive,
  groupFooter,
  groupActions,
  sectionTitle,
  bulkActions: bulkActionsProp,
  bulkSelectionNoun,
  canSelectRow,
  rowDrag: rowDragProp,
  pressOnlyOnTouch = false,
  hideGroupCount,
  paddingX,
  paddingXAlign = "edge",
  gutterTop,
  gutterBottom,
  groupCounts,
  lazyGroups,
  onGroupActivate,
  emptyGroupsStartOpen = false,
  collapsedGroupKeys,
  onCollapsedGroupKeysChange,
}: DataTableProps<TData>) {
  // `enclosed`: the group bands and the last row draw hairlines and the
  // column header's own line goes, since the bands' lines take over the
  // separation and a second line would read as a double border.
  const enclosed = borders === "enclosed";
  const hideHeaderBorder = enclosed;
  const showLastRowBorder = enclosed;
  const groupHeaderBorder = enclosed;
  // Drag affordances (header reorder, resize handles) are pointer-shaped UI —
  // gate them on touch capability, not viewport width. A narrow desktop
  // window keeps them; an iPad in landscape (>768px) gets the settings
  // panel's up/down buttons instead of a dead drag.
  const isTouch = useIsTouchDevice();
  // A press-only table on touch drops everything a held press would reach.
  const pressOnly = pressOnlyOnTouch && isTouch;
  const bulkActions = pressOnly ? undefined : bulkActionsProp;
  const rowDrag = pressOnly ? undefined : rowDragProp;
  const renderRowActions = pressOnly ? undefined : renderRowActionsProp;
  const hasBulkActions = !!bulkActions?.length;
  const fitContent = scrollMode === "fit";
  // Column pinning applies at EVERY viewport (reference parity), left and
  // right alike. A narrow window is exactly where a frozen name lane earns
  // its keep, and stripping the pins there is what makes the checkbox and
  // name pan away with the columns. Do not re-add a viewport gate to either
  // edge.

  // When bulkActions are provided, manage selection state per-group so that
  // one group's table operations never interfere with another group's
  // selections (important when pages are lazy-loaded).
  const [groupSelections, setGroupSelections] = React.useState<
    Record<string, RowSelectionState>
  >({});

  // Track which groups have "select all" active (meaning ALL items including
  // unloaded ones, not just visible rows).
  const [selectAllGroupKeys, setSelectAllGroupKeys] = React.useState<
    Set<string>
  >(new Set());

  // Stable per-group onRowSelectionChange handlers (cached in a ref so the
  // same function reference is returned for a given groupKey across renders).
  const groupSelectionHandlersRef = React.useRef(
    new Map<string, OnChangeFn<RowSelectionState>>(),
  );
  const getGroupSelectionHandler = React.useCallback(
    (groupKey: string): OnChangeFn<RowSelectionState> => {
      let handler = groupSelectionHandlersRef.current.get(groupKey);
      if (!handler) {
        handler = (updater) => {
          setGroupSelections((prev) => ({
            ...prev,
            [groupKey]:
              typeof updater === "function"
                ? updater(prev[groupKey] ?? {})
                : updater,
          }));
        };
        groupSelectionHandlersRef.current.set(groupKey, handler);
      }
      return handler;
    },
    [],
  );

  // Merged selection across all groups (for bulk actions bar + row data cache)
  const mergedRowSelection = React.useMemo(() => {
    const merged: RowSelectionState = {};
    for (const sel of Object.values(groupSelections)) {
      Object.assign(merged, sel);
    }
    return merged;
  }, [groupSelections]);

  // -------------------------------------------------------------------------
  // Lazy group activation + resolved groups
  // -------------------------------------------------------------------------

  const [activatedKeys, setActivatedKeys] = React.useState<Set<string>>(
    new Set(),
  );
  const onGroupActivateRef = React.useRef(onGroupActivate);
  onGroupActivateRef.current = onGroupActivate;

  const handleGroupActivate = React.useCallback((key: string) => {
    setActivatedKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    onGroupActivateRef.current?.(key);
  }, []);

  // Loading with no groups at all: the group set is still resolving, so
  // there is nothing real to render — stand in with skeleton groups rather
  // than a blank body. Their skeletons are delayed, so a fast resolve swaps
  // the real groups in before any shimmer paints.
  const skeletonPhase = loading && groups.length === 0;

  const resolvedGroups = React.useMemo(() => {
    if (skeletonPhase) return SKELETON_GROUPS as DataTableGroup<TData>[];
    let resolved = groups;

    // Apply groupCounts to groups that don't already have a totalCount
    if (groupCounts) {
      resolved = resolved.map((g) => {
        const count = groupCounts[g.key];
        if (count !== undefined && g.totalCount === undefined) {
          return { ...g, totalCount: count };
        }
        return g;
      });
    }

    // Wire up lazy activation for placeholder groups
    if (lazyGroups) {
      resolved = resolved.map((g) => {
        if (!g.placeholder) return g;
        if (activatedKeys.has(g.key)) return g;
        return {
          ...g,
          fetchPage: () => handleGroupActivate(g.key),
        };
      });
    }

    return resolved;
  }, [
    skeletonPhase,
    groups,
    groupCounts,
    lazyGroups,
    activatedKeys,
    handleGroupActivate,
  ]);

  // Accumulate selected row data across page loads so the bulk actions bar
  // can resolve rows even after their page has scrolled out of view.
  const selectedRowDataRef = React.useRef(new Map<string, TData>());
  if (hasBulkActions && getRowId) {
    const sel = mergedRowSelection;
    const cache = selectedRowDataRef.current;
    // Remove deselected entries
    for (const id of cache.keys()) {
      if (!sel[id]) cache.delete(id);
    }
    // Capture data for any selected rows visible in current groups
    for (const group of resolvedGroups) {
      for (const [i, row] of group.data.entries()) {
        const id = getRowId(row, i);
        if (sel[id]) cache.set(id, row);
      }
    }
  }

  // Auto-prepend selection column and auto-append actions column. The
  // actions column also mounts without row actions when the table opted into
  // the circle-plus column picker — its header is the picker's home.
  const columns = React.useMemo(() => {
    let cols = columnsProp;
    if (hasBulkActions) {
      if (!cols.some((c) => c.id === "select")) {
        cols = [createSelectionColumn<TData>(), ...cols];
      }
    }
    if (renderRowActions || onColumnVisibilityChange || addColumnHeader != null) {
      cols = [...cols, createActionsColumn<TData>()];
    }
    return cols;
  }, [
    columnsProp,
    hasBulkActions,
    renderRowActions,
    onColumnVisibilityChange,
    addColumnHeader,
  ]);

  const handleClearSelection = React.useCallback(() => {
    setGroupSelections({});
    setSelectAllGroupKeys(new Set());
  }, []);

  // Stable per-group setSelectAll callbacks (cached in a ref).
  const selectAllHandlersRef = React.useRef(
    new Map<string, (value: boolean) => void>(),
  );
  const getSelectAllHandler = React.useCallback((groupKey: string) => {
    let handler = selectAllHandlersRef.current.get(groupKey);
    if (!handler) {
      handler = (value: boolean) => {
        setSelectAllGroupKeys((prev) => {
          const next = new Set(prev);
          if (value) next.add(groupKey);
          else next.delete(groupKey);
          return next;
        });
      };
      selectAllHandlersRef.current.set(groupKey, handler);
    }
    return handler;
  }, []);

  // Derive selectAllGroups for the bulk actions bar.
  const selectAllGroups = React.useMemo<SelectAllGroup[]>(() => {
    if (selectAllGroupKeys.size === 0) return [];
    return resolvedGroups
      .filter((g) => selectAllGroupKeys.has(g.key))
      .map((g) => ({
        groupKey: g.key,
        totalCount:
          typeof g.totalCount === "number" ? g.totalCount : g.data.length,
      }));
  }, [resolvedGroups, selectAllGroupKeys]);

  const isUngrouped =
    resolvedGroups.length === 1 && resolvedGroups[0]?.key === ALL_GROUP_KEY;

  // Row drag needs groups to land on; an ungrouped table has nowhere to go.
  const rowDragEnabled = rowDrag !== undefined && !isUngrouped;

  // Grouped `sharedHeader` tables render ONE column header (hoisted above the
  // first group), so its select-all checkbox must address every group — the
  // group-scoped logic it mounts inside can only reach group 0. Null outside
  // that shape; the header checkbox then keeps its group-scoped behavior.
  const sharedSelectAll =
    React.useMemo<DataTableSharedSelectAllValue | null>(() => {
      if (!sharedHeader || isUngrouped || !hasBulkActions || !getRowId)
        return null;
      let selectableCount = 0;
      let selectedCount = 0;
      for (const g of resolvedGroups) {
        for (const [i, row] of g.data.entries()) {
          if (canSelectRow && !canSelectRow(row)) continue;
          selectableCount++;
          if (mergedRowSelection[getRowId(row, i)]) selectedCount++;
        }
      }
      const isAllSelected =
        selectableCount > 0 && selectedCount === selectableCount;
      return {
        isAllSelected,
        isSomeSelected: selectedCount > 0 && !isAllSelected,
        toggle: (value: boolean) => {
          if (!value) {
            setGroupSelections({});
            setSelectAllGroupKeys(new Set());
            return;
          }
          // Mirror the per-group header semantics: loaded selectable rows
          // check on, and every group flips to select-all so unloaded pages
          // count too (the bulk bar's selectAllGroups totals).
          const nextSelections: Record<string, RowSelectionState> = {};
          const nextKeys = new Set<string>();
          for (const g of resolvedGroups) {
            const sel: RowSelectionState = {};
            for (const [i, row] of g.data.entries()) {
              if (canSelectRow && !canSelectRow(row)) continue;
              sel[getRowId(row, i)] = true;
            }
            nextSelections[g.key] = sel;
            nextKeys.add(g.key);
          }
          setGroupSelections(nextSelections);
          setSelectAllGroupKeys(nextKeys);
        },
      };
    }, [
      sharedHeader,
      isUngrouped,
      hasBulkActions,
      getRowId,
      canSelectRow,
      resolvedGroups,
      mergedRowSelection,
    ]);

  // -------------------------------------------------------------------------
  // Container width measurement
  // -------------------------------------------------------------------------

  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [, notifyScrollMount] = React.useReducer((x: number) => x + 1, 0);
  const didNotifyScrollMountRef = React.useRef(false);
  // The scrollport's CONTENT width: `clientWidth` excludes the scrollbar, so
  // the measurement moves when a scrollbar appears or disappears — something
  // a resize event alone misses. Zero until the first layout-effect
  // measurement, which lands before the first paint.
  const { ref: containerSizeRef, size: containerSize } =
    useElementSize<HTMLDivElement>({ box: "content" });
  const containerWidth = containerSize.width;

  const scrollRefCallback = React.useCallback(
    (node: HTMLDivElement | null) => {
      scrollContainerRef.current = node;
      containerSizeRef(node);
      if (node) {
        // When the scroll container mounts, force all children (virtualizers)
        // to re-render so they can pick up the now-available scroll element.
        if (!didNotifyScrollMountRef.current) {
          didNotifyScrollMountRef.current = true;
          notifyScrollMount();
        }
        return;
      }
      didNotifyScrollMountRef.current = false;
    },
    [notifyScrollMount, containerSizeRef],
  );

  const {
    visibility,
    tableColumnOrder,
    columnPinning,
    mergedOrder,
    visualLayout,
    totalResolvedWidth,
    headerCellStyles,
    bodyCellStyles,
    reorderableIdSet,
    resizeController,
    activeResizeColumnId,
    draggingColumnId,
    dropIndicatorX,
    didDragRef,
    ghostRefCallback,
    draggingColumnLabel,
    draggingColumnRect,
    handleColumnDrop,
  } = useDataTableLayout({
    columns,
    columnsProp,
    columnOrderProp,
    onColumnOrderChange,
    columnVisibilityProp,
    onColumnVisibilityChange,
    addColumnHeader,
    columnSizingProp,
    onColumnSizingChange,
    pinnedColumnIds,
    fillColumnId,
    columnLayout,
    renderRowActions,
    containerWidth,
    scrollContainerRef,
    scrollFade,
    scrollMode,
    isTouch,
  });

  // Group-header offsets for the select-column shape: the chevron (16px)
  // centers on the checkbox column's content center, and the leading box ends
  // one gap (8px, the header control's gap-sm) before the first consumer
  // column's text line — so group titles land exactly on the rows' text line.
  // --- Sections: the tier above the groups -----------------------------------
  // Consecutive groups sharing a `section` render inside one bordered card
  // under one band; the runs are computed here so the render below is a flat
  // map. Section collapse is table-local session state — a closed section
  // unmounts its groups (a closed card shows nothing, so nothing loads).
  const groupRuns = React.useMemo(() => {
    type Run = {
      section: DataTableSection | undefined;
      groups: { group: DataTableGroup<TData>; groupIndex: number }[];
      count: number | undefined;
    };
    const runs: Run[] = [];
    resolvedGroups.forEach((group, groupIndex) => {
      const last = runs[runs.length - 1];
      if (last && last.section?.key === group.section?.key) {
        last.groups.push({ group, groupIndex });
      } else {
        runs.push({ section: group.section, groups: [{ group, groupIndex }], count: undefined });
      }
    });
    for (const run of runs) {
      if (!run.section) continue;
      // A summed count means something only once every group reports one.
      const totals = run.groups.map(({ group }) => group.totalCount);
      run.count = totals.every((t) => typeof t === "number")
        ? totals.reduce<number>((sum, t) => sum + (t as number), 0)
        : undefined;
    }
    return runs;
  }, [resolvedGroups]);
  const [collapsedSectionKeys, setCollapsedSectionKeys] = React.useState<
    Set<string>
  >(() => new Set());
  const toggleSection = React.useCallback((key: string) => {
    setCollapsedSectionKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const groupLeading = React.useMemo(() => {
    if (isUngrouped || !visualLayout.visualIds.includes("select")) return null;
    const paddingXPx = paddingX ? PADDING_X_PX[paddingX] : 0;
    return {
      width: Math.max(
        0,
        SELECTION_COLUMN_WIDTH + DENSITY_CONFIG[density].cellPadLeftPx - paddingXPx - 8,
      ),
      chevronMargin: Math.max(0, (SELECTION_COLUMN_WIDTH - paddingXPx) / 2 - 8),
    };
  }, [isUngrouped, visualLayout, density, paddingX]);

  // -------------------------------------------------------------------------
  // Group open/close state
  // -------------------------------------------------------------------------

  // A group's rows are PROVEN when it has settled: not loading — a loading
  // group's numeric totalCount is a provisional 0 while the count is in
  // flight, so the number is only trustworthy once the group settles — and
  // its resolved count (or, uncounted, its fetched rows) is non-empty. The
  // start-open heuristic and the settled-open latch below both read
  // row-presence through this one predicate, so they cannot drift apart.
  const groupHasSettledRows = React.useCallback(
    (g: DataTableGroup<TData>) =>
      !g.loading &&
      (typeof g.totalCount === "number"
        ? g.totalCount > 0
        : g.data.length > 0),
    [],
  );

  // Empty groups START collapsed: their expanded body is just the "+ Add …"
  // row, so open-by-default reads noisy at scale. "Empty" means KNOWN empty
  // — a loading group or an uncounted placeholder may have rows on the way
  // and starts open; that optimism is exactly what the settled latch below
  // omits. A non-loading placeholder's count IS trusted — placeholders size
  // themselves from it. `emptyGroupsStartOpen` opts a canonical-roster
  // grouping out of the heuristic: every group starts open, and only an
  // explicit collapse closes one. The single ungrouped group always starts
  // open: collapsed, an ungrouped table would render nothing (its empty
  // state included) with no band to reopen it from.
  const groupStartsOpen = React.useCallback(
    (g: DataTableGroup<TData>) =>
      emptyGroupsStartOpen ||
      g.loading ||
      groupHasSettledRows(g) ||
      (typeof g.totalCount !== "number" && g.placeholder === true),
    [emptyGroupsStartOpen, groupHasSettledRows],
  );

  const defaultOpenGroupKeys = () => {
    if (isUngrouped) return new Set(resolvedGroups.map((g) => g.key));
    return new Set(
      resolvedGroups.filter(groupStartsOpen).map((g) => g.key),
    );
  };

  const [openGroupKeys, setOpenGroupKeys] = React.useState<Set<string>>(
    defaultOpenGroupKeys,
  );

  // Controlled collapse (`collapsedGroupKeys`) carries only EXPLICIT
  // collapses — the set a consumer persists. Groups outside it keep the
  // default-open heuristic above, so a known-empty group still starts closed
  // without an entry landing in the consumer's set. Opening one of those
  // heuristic-closed groups is a session act, tracked here: reported, it
  // would make "peeked into an empty group" indistinguishable from a change
  // worth persisting.
  const [sessionExpandedKeys, setSessionExpandedKeys] = React.useState<
    Set<string>
  >(() => new Set());

  // Groups seen SETTLED non-empty latch open: once a group has proven rows,
  // the start-collapsed heuristic never re-applies to it — a search with no
  // hits must keep every band and its add-row live, not snap them shut.
  // Closing a latched group is the user's explicit act. The latch trusts
  // only settled evidence (a resolved count, fetched rows) — never the
  // loading/placeholder optimism `groupStartsOpen` grants first paint,
  // because an unfetched placeholder has not settled anything.
  //
  // An idempotent accumulator, mutated during render (the loop below the
  // reset block). The open-set memo reads it without listing it, which is
  // safe because its content is a pure function of `resolvedGroups` — a dep
  // the memo already carries — so the ref cannot change without the memo
  // re-running. Feeding it from anything outside that dep list would
  // silently freeze the memo. Under concurrent rendering an abandoned render
  // can latch a state the user never saw; accepted — the cost is a group
  // staying open.
  const settledOpenKeysRef = React.useRef<Set<string>>(new Set());

  // When groups change (e.g. groupBy field changed), reset to default state.
  // Sits between the latch's declaration and its accumulation so the wipe
  // lands before this render's keys are recorded — ordered after the loop,
  // the new group set's keys would write into the old ref only to be
  // cleared in the same pass.
  const prevGroupKeysRef = React.useRef<string>("");
  const currentGroupKeysStr = groups.map((g) => g.key).join("\0");
  if (prevGroupKeysRef.current !== currentGroupKeysStr) {
    prevGroupKeysRef.current = currentGroupKeysStr;
    // Scroll to top when the group set actually changes (field change, sort direction)
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = 0;
    setOpenGroupKeys(defaultOpenGroupKeys());
    setSessionExpandedKeys(new Set());
    settledOpenKeysRef.current = new Set();
    if (lazyGroups) setActivatedKeys(new Set());
  }

  for (const g of resolvedGroups) {
    if (groupHasSettledRows(g)) {
      settledOpenKeysRef.current.add(g.key);
    }
  }

  // The open set every consumer below reads — controlled mode derives it per
  // render (explicit collapses win, then the session overlay, then the
  // heuristic; ungrouped tables have no collapse), uncontrolled mode is the
  // state above.
  const resolvedOpenGroupKeys = React.useMemo(() => {
    if (!collapsedGroupKeys) return openGroupKeys;
    return new Set(
      resolvedGroups
        .filter(
          (g) =>
            !collapsedGroupKeys.has(g.key) &&
            (isUngrouped ||
              sessionExpandedKeys.has(g.key) ||
              settledOpenKeysRef.current.has(g.key) ||
              groupStartsOpen(g)),
        )
        .map((g) => g.key),
    );
  }, [
    collapsedGroupKeys,
    openGroupKeys,
    resolvedGroups,
    sessionExpandedKeys,
    isUngrouped,
    groupStartsOpen,
  ]);

  const collapseAll = React.useCallback(() => {
    if (collapsedGroupKeys) {
      setSessionExpandedKeys(new Set());
      onCollapsedGroupKeysChange?.(new Set(resolvedGroups.map((g) => g.key)));
      return;
    }
    setOpenGroupKeys(new Set());
  }, [collapsedGroupKeys, onCollapsedGroupKeysChange, resolvedGroups]);

  const expandAll = React.useCallback(() => {
    if (collapsedGroupKeys) {
      // Expand-all opens the heuristic-closed groups too, so the session
      // overlay takes every key; only the explicit set empties upward.
      setSessionExpandedKeys(new Set(resolvedGroups.map((g) => g.key)));
      if (collapsedGroupKeys.size > 0) onCollapsedGroupKeysChange?.(new Set());
      return;
    }
    setOpenGroupKeys(new Set(resolvedGroups.map((g) => g.key)));
  }, [collapsedGroupKeys, onCollapsedGroupKeysChange, resolvedGroups]);

  const handleGroupToggle = React.useCallback(
    (key: string) => {
      if (collapsedGroupKeys) {
        if (resolvedOpenGroupKeys.has(key)) {
          setSessionExpandedKeys((prev) => {
            if (!prev.has(key)) return prev;
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
          // Only a group that would otherwise stay open — proven rows, or
          // the start-open heuristic — collapses into the explicit set. A
          // group that closes on its own once the session peek clears needs
          // no entry, and reporting one would turn "peeked into an empty
          // group and closed it" into an edit worth persisting.
          const group = resolvedGroups.find((g) => g.key === key);
          if (
            settledOpenKeysRef.current.has(key) ||
            (group ? groupStartsOpen(group) : true)
          ) {
            onCollapsedGroupKeysChange?.(new Set(collapsedGroupKeys).add(key));
          }
        } else {
          setSessionExpandedKeys((prev) => new Set(prev).add(key));
          if (collapsedGroupKeys.has(key)) {
            const next = new Set(collapsedGroupKeys);
            next.delete(key);
            onCollapsedGroupKeysChange?.(next);
          }
        }
        return;
      }
      setOpenGroupKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    },
    [
      collapsedGroupKeys,
      onCollapsedGroupKeysChange,
      resolvedOpenGroupKeys,
      resolvedGroups,
      groupStartsOpen,
    ],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const renderGroup = (group: DataTableGroup<TData>, groupIndex: number) => (
              <DataTableSelectAllProvider
                key={group.key}
                isSelectAll={selectAllGroupKeys.has(group.key)}
                setSelectAll={getSelectAllHandler(group.key)}
              >
                <DataTableGroupContent
                  group={group}
                  groupKey={group.key}
                  columns={columns}
                  isExpanded={resolvedOpenGroupKeys.has(group.key)}
                  density={density}
                  getRowId={getRowId}
                  sorting={sorting}
                  onSortingChange={onSortingChange}
                  rowSelection={
                    hasBulkActions
                      ? (groupSelections[group.key] ?? EMPTY_ROW_SELECTION)
                      : undefined
                  }
                  onRowSelectionChange={
                    hasBulkActions
                      ? getGroupSelectionHandler(group.key)
                      : undefined
                  }
                  canSelectRow={canSelectRow}
                  columnPinning={columnPinning}
                  tableColumnOrder={tableColumnOrder}
                  tableColumnVisibility={visibility}
                  headerCellStyles={headerCellStyles}
                  bodyCellStyles={bodyCellStyles}
                  resizeController={resizeController}
                  activeResizeColumnId={activeResizeColumnId}
                  draggingColumnId={draggingColumnId}
                  canResizeColumns={!isTouch}
                  columnLayout={columnLayout}
                  groupLeading={groupLeading}
                  reorderableIdSet={reorderableIdSet}
                  reorderableIds={visualLayout.reorderableIds}
                  onColumnMove={handleColumnDrop}
                  renderColumnMenuItems={renderColumnMenuItems}
                  canHideColumns={onColumnVisibilityChange !== undefined}
                  containerWidth={containerWidth}
                  loading={loading}
                  emptyState={emptyState}
                  onRowClick={onRowClick}
                  rowClickable={rowClickable}
                  extraData={extraData}
                  scrollContainerRef={scrollContainerRef}
                  groupTitle={groupTitle}
                  groupTitleInteractive={groupTitleInteractive}
                  groupFooter={groupFooter}
                  groupActions={groupActions}
                  onToggle={handleGroupToggle}
                  collapseAll={collapseAll}
                  expandAll={expandAll}
                  allCollapsed={resolvedOpenGroupKeys.size === 0}
                  stickyHeader={stickyHeader}
                  scrollMode={scrollMode}
                  hideHeader={hideHeader}
                  hideHeaderBorder={hideHeaderBorder}
                  sharedHeader={sharedHeader}
                  showLastRowBorder={showLastRowBorder}
                  groupHeaderBorder={groupHeaderBorder}
                  groupGap={groupGap}
                  isUngrouped={isUngrouped}
                  hideGroupCount={hideGroupCount}
                  paddingX={paddingX}
                  paddingXAlign={paddingXAlign}
                  renderRowActions={renderRowActions}
                  armRowsOnHold={!pressOnly}
                  rowDragEnabled={rowDragEnabled}
                  groupIndex={groupIndex}
                  bandInsetPx={group.section ? SECTION_BAND_INSET_PX : 0}
                />
              </DataTableSelectAllProvider>
  );

  // The scroll container doubles as the table's single `ContextMenuTrigger`
  // (see `DataTableOverlayProvider`), so it is built here and handed over
  // rather than nested inline.
  //
  // It is `relative` so the veil/indicator/ghost overlays position in content
  // coordinates and scroll with the rows, and takes `fill="flex"` (flex-1 PLUS
  // min-h-0/min-w-0), never a bare `flex-1`. A flex item's default
  // `min-height: auto` resolves to its content height, so a bare `flex-1`
  // refuses to shrink into the space the parent offers and stretches to the
  // full table height instead — `overflow-auto` then never has anything to
  // clip, the virtualizer measures a viewport as tall as the whole dataset,
  // and virtualization silently becomes a no-op that renders EVERY row.
  // `min-h-0` is what keeps the scroll box bounded.
  const scrollContainer = (
      <Box position="relative" direction="column"
        ref={scrollRefCallback}
        data-testid={dataTestId}
        fill={fitContent ? undefined : "flex"}
        className={cn(
          SURFACE_BG[surface],
          SURFACE_FILL_VAR[surface],
          // The scroll box is the size container `gutterBottom`'s half-height
          // slack measures. Only a table with that slack wears it: size
          // containment zeroes the box's content height, so a table whose
          // host has no definite height would render no rows.
          scrollMode === "internal" && gutterBottom && SCROLLPORT_CONTAINER_CLASS,
          scrollMode === "internal"
            ? // During a resize drag, force overflow-auto so a column
              // growing past the container isn't clipped mid-gesture.
              totalResolvedWidth > containerWidth ||
              activeResizeColumnId !== null
              ? "overflow-auto"
              : "overflow-y-auto overflow-x-hidden"
            : "overflow-visible",
          // On a narrow container a pan settles on a whole column instead of
          // two slivers. Proximity, not mandatory: free flicks keep their
          // momentum and only a near-boundary release is nudged home. The
          // snap areas are the unpinned header cells; scroll-padding-left is
          // written with the sticky offsets so a snapped column lands at the
          // pinned lane's edge, not under it. Wider containers get no snap —
          // desktop trackpad panning stays untouched.
          scrollMode === "internal" && containerWidth < NARROW_CONTAINER_WIDTH
            ? "snap-x snap-proximity"
            : null,
          // The edge fade reads the overflow lengths the effect above writes;
          // `0px` fallbacks keep the mask a no-op before the first tick.
          scrollFade &&
            scrollMode === "internal" &&
            "[--fade-size:1.5rem] mask-b-from-[calc(100%-min(var(--fade-size),var(--dt-overflow-end,0px)))]",
          scrollFade &&
            scrollMode === "internal" &&
            !stickyHeader &&
            "mask-t-from-[calc(100%-min(var(--fade-size),var(--dt-overflow-start,0px)))]",
          "[overscroll-behavior-x:none]",
          "[scrollbar-width:thin]",
          "[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5",
          "[&::-webkit-scrollbar-track]:bg-transparent",
          "[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30",
          "[&::-webkit-scrollbar-corner]:bg-transparent",
        )}
      >
          <DataTableHeaderDragProvider
            didDragRef={didDragRef}
            reorderableIds={reorderableIdSet}
          >
            {/* The top gutter is scroll content, not container chrome — the
                counterpart of the bulk-bar clearance below the groups — so
                rows scroll up through it and sticky headers still pin to the
                table edge. */}
            {gutterTop && scrollMode === "internal" ? (
              <Box paddingTop={gutterTop} shrink={false} />
            ) : null}
            <DataTableSharedSelectAllProvider value={sharedSelectAll}>
            <RowDragBoundary
              config={rowDragEnabled ? rowDrag : undefined}
              gutterPx={paddingX ? PADDING_X_PX[paddingX] : 0}
            >
            {groupRuns.map((run) => {
              if (!run.section) {
                return run.groups.map(({ group, groupIndex }) =>
                  renderGroup(group, groupIndex),
                );
              }
              const isExpanded = !collapsedSectionKeys.has(run.section.key);
              return (
                <Box
                  key={`section:${run.section.key}`}
                  data-slot="table-section"
                  border
                  rounded="lg"
                  marginX={SECTION_MARGIN_X}
                  marginBottom="md"
                  paddingBottom={isExpanded ? "sm" : undefined}
                >
                  <DataTableSectionHeader
                    title={
                      sectionTitle
                        ? sectionTitle(run.section.key)
                        : run.section.label
                    }
                    count={hideGroupCount ? undefined : run.count}
                    isExpanded={isExpanded}
                    onToggle={() => toggleSection(run.section!.key)}
                    paddingX={paddingX}
                  />
                  {isExpanded
                    ? run.groups.map(({ group, groupIndex }) =>
                        renderGroup(group, groupIndex),
                      )
                    : null}
                </Box>
              );
            })}
            </RowDragBoundary>
            {/* The bottom gutter mirrors the top one: scroll content, so the
                last row can travel up past the box's edge — and, once the
                rows already overflow the box (`data-has-overflow-y`, written
                by the layout hook), half the box of slack on top, so the
                last row can travel up to its middle. A table that fits
                keeps the fixed gutter alone as its foot. */}
            {gutterBottom && scrollMode === "internal" ? (
              <Box
                paddingBottom={gutterBottom}
                shrink={false}
                // `SCROLL_END_SLACK_CLASS`, behind the overflow gate.
                className="[[data-has-overflow-y]>&]:min-h-[50cqh]"
              />
            ) : null}
            </DataTableSharedSelectAllProvider>
            {hasBulkActions && getRowId ? (
              <Box height={BULK_ACTIONS_BAR_CLEARANCE} shrink={false} />
            ) : null}
          </DataTableHeaderDragProvider>
          {draggingColumnRect !== null ? (
            // Dimming veil over the dragged column — reads as "lifted".
            <Box pointerEvents="none" position="absolute" zIndex={15}
              className={cn("top-0 bottom-0", SURFACE_VEIL[surface],
              )}
              style={{
                left: draggingColumnRect.left,
                width: draggingColumnRect.width,
              }}
            />
          ) : null}
          {dropIndicatorX !== null ? (
            <Box pointerEvents="none" position="absolute" zIndex={50} rounded="full" duration={150}
              className="top-0 bottom-0 w-0.5 bg-brand transition-[left] ease-out"
              style={{ left: dropIndicatorX - 1 }}
            >
              <Box position="absolute" rounded="full" className="top-0 -left-0.5 size-1.5 bg-brand" />
            </Box>
          ) : null}
          {draggingColumnId !== null ? (
            // Floating copy of the dragged column's header, positioned
            // imperatively by useColumnReorder's rAF loop.
            <Box pointerEvents="none" position="absolute" zIndex={60}
              ref={ghostRefCallback}
              surface="popover"
              rounded="md"
              border
              paddingX="sm"
              paddingY="2xs"
              className="top-0 left-0 will-change-transform"
            >
              <Text size="sm" weight="medium" truncate>
                {draggingColumnLabel}
              </Text>
            </Box>
          ) : null}
      </Box>
  );

  return (
    // Root is a flex column: the `flex-1` stretch chain and the empty state's
    // fill depend on it. Same `fill="flex"` rule as the scroll container above.
    <Box position="relative" direction="column"
      fill={fitContent ? undefined : "flex"}
    >
      {/* Surface and density both wrap the overlay host, not just the scroll
          container: the popups render from the host, OUTSIDE the scrolling
          subtree, and still have to resolve the same surface and the same density
          as the cells they came from. */}
      <DataTableSurfaceProvider surface={surface}>
        <DataTableDensityProvider density={density}>
          {/* The circle-plus column picker (the actions column's header slot)
              reads its model from here — null (renders nothing) unless the
              consumer passed onColumnVisibilityChange or its own
              addColumnHeader node. */}
          <DataTableColumnPickerProvider
            columns={columnsProp}
            columnOrder={mergedOrder}
            columnVisibility={visibility}
            pinnedColumnIds={pinnedColumnIds}
            onColumnVisibilityChange={onColumnVisibilityChange}
            onColumnOrderChange={onColumnOrderChange}
            addColumnHeader={addColumnHeader}
            resizing={activeResizeColumnId !== null}
          >
            <DataTableOverlayProvider scrollContainer={scrollContainer} />
          </DataTableColumnPickerProvider>
        </DataTableDensityProvider>

        {hasBulkActions && getRowId && (
          <BulkActionsBar
            actions={bulkActions}
            selectionNoun={bulkSelectionNoun}
            // The bar takes plain ids; the table's TanStack selection map
            // reduces to its truthy keys.
            selectedIds={
              new Set(
                Object.keys(mergedRowSelection).filter(
                  (id) => mergedRowSelection[id],
                ),
              )
            }
            onClearSelection={handleClearSelection}
            selectedRowData={selectedRowDataRef.current}
            selectAllGroups={selectAllGroups}
          />
        )}
      </DataTableSurfaceProvider>
    </Box>
  );
}

DataTable.displayName = "DataTable";

export { DataTableCell };
